import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  MenuItem,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery,
} from "@mui/material";
import AssignmentTurnedInIcon from "@mui/icons-material/AssignmentTurnedIn";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CloseIcon from "@mui/icons-material/Close";
import FactCheckIcon from "@mui/icons-material/FactCheck";
import ImageIcon from "@mui/icons-material/Image";
import Inventory2Icon from "@mui/icons-material/Inventory2";
import PlaylistAddCheckIcon from "@mui/icons-material/PlaylistAddCheck";
import RuleIcon from "@mui/icons-material/Rule";
import SaveIcon from "@mui/icons-material/Save";
import TimelineIcon from "@mui/icons-material/Timeline";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import VisibilityIcon from "@mui/icons-material/Visibility";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";

import { axiosInstance } from "../../../../../api/axios";
import {
  getRiskStandardBoxSx,
  getRiskStandardChipSx,
  getRiskStandardStyle,
} from "../../../utils/riskStandard";
import { EmployeeSelect } from "../../../components/common/EmployeeSelect";
import { ManualHelpButton } from "../../../components/common/HelperFunc";

const todayText = () => {
  const date = new Date();
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(
    date.getDate()
  ).padStart(2, "0")}`;
};

const todayMonthText = () => todayText().slice(0, 7);
const monthToEvalDate = (month) => `${month || todayMonthText()}-01`;

const toNumber = (value) => {
  const number = Number(value);
  return Number.isFinite(number) ? number : 0;
};

const isAfterRiskScoreValid = (value) => {
  const score = Number(value);
  return Number.isInteger(score) && score >= 1 && score <= 20;
};

const isFinalizedEvaluationRow = (row = {}) =>
  toNumber(row?.eval_status) >= 20 ||
  toNumber(row?.improve_status) >= 20 ||
  Boolean(row?.improve_after && row?.comp_date);

const getRowKey = (row) => String(row?.__lineGroupKey || row?.eval_id || `cause-${row?.register_cause_id}`);
const getSelectionKey = (row) => String(row?.__lineGroupKey || row?.register_cause_id || getRowKey(row));
const getMachineName = (row) =>
  row?.safe_machine_name || row?.machine_name || row?.machine_manage_code || "-";
const getProcessName = (row) => row?.process_name || row?.process_description || "-";
const getRiskScore = (row) =>
  toNumber(row?.risk_score) ||
  toNumber(row?.frequency_level) * toNumber(row?.importancy_level);

const getRiskPreview = (row, form = {}, criteria = {}) => {
  const frequency =
    (criteria.frequencies || []).find(
      (item) => Number(item.frequency_id) === Number(form.frequency_id)
    ) || null;
  const importancy =
    (criteria.importancies || []).find(
      (item) => Number(item.importancy_id) === Number(form.importancy_id)
    ) || null;
  const score =
    toNumber(row?.risk_score) ||
    toNumber(frequency?.frequency_level || row?.frequency_level) *
      toNumber(importancy?.importancy_level || row?.importancy_level);
  const standard =
    (criteria.standards || []).find(
      (item) =>
        score &&
        score >= toNumber(item.risk_level_min) &&
        score <= toNumber(item.risk_level_max)
    ) || null;

  return { frequency, importancy, score, standard };
};

const toArray = (value) => {
  if (Array.isArray(value)) return value.filter((item) => item !== undefined && item !== null && item !== "");
  if (value === undefined || value === null || value === "") return [];
  return String(value)
    .split(/\s*,\s*|\s*\/\s*|\s*>\s*/)
    .map((item) => item.trim())
    .filter(Boolean);
};

const getCommonValue = (rows = [], key) => {
  const values = rows
    .map((row) => row?.[key])
    .filter((value) => value !== undefined && value !== null && value !== "");
  if (!values.length) return "";
  const first = String(values[0]);
  return values.every((value) => String(value) === first) ? values[0] : "";
};

const getLineEntriesFromRow = (row = {}) => {
  const entries = [];
  const pushEntry = (id, name) => {
    const numericId = Number(id);
    const entryId = Number.isInteger(numericId) && numericId > 0 ? numericId : null;
    const entryName = String(name || "").trim();
    if (!entryId && !entryName) return;
    const key = entryId ? `id-${entryId}` : `name-${entryName}`;
    if (entries.some((entry) => entry.key === key)) return;
    entries.push({ key, id: entryId, name: entryName || `라인 ${entryId}` });
  };

  pushEntry(row.source_location_id, row.source_location_name);

  const ids = toArray(row.location_ids);
  const names = toArray(row.location_names || row.location_summary);
  const maxLength = Math.max(ids.length, names.length);
  for (let index = 0; index < maxLength; index += 1) {
    pushEntry(ids[index], names[index]);
  }

  return entries;
};

const isStep1EvaluationComplete = (row = {}) =>
  Boolean(row.eval_id && row.frequency_id && row.importancy_id);

const createStepLineRows = (rows = []) => {
  const lineMap = new Map();

  rows.forEach((row) => {
    getLineEntriesFromRow(row).forEach((entry) => {
      const groupKey = `line-${entry.key}`;
      if (!lineMap.has(groupKey)) {
        lineMap.set(groupKey, {
          __lineGroup: true,
          __lineGroupKey: groupKey,
          eval_scope: "line",
          source_location_id: entry.id,
          source_location_name: entry.name,
          items: [],
        });
      }
      lineMap.get(groupKey).items.push(row);
    });
  });

  return Array.from(lineMap.values()).map((group) => {
    const items = group.items;
    const first = items[0] || {};
    const machineNames = new Set(items.map(getMachineName).filter((name) => name && name !== "-"));
    const processNames = new Set(items.map(getProcessName).filter((name) => name && name !== "-"));
    const completedCount = items.filter(isStep1EvaluationComplete).length;
    const pendingCount = Math.max(items.length - completedCount, 0);

    return {
      ...first,
      ...group,
      register_cause_id: first.register_cause_id,
      eval_id: getCommonValue(items, "eval_id"),
      frequency_id: getCommonValue(items, "frequency_id"),
      importancy_id: getCommonValue(items, "importancy_id"),
      standard_id: getCommonValue(items, "standard_id"),
      wizard_step: Math.max(...items.map((item) => toNumber(item.wizard_step))),
      wizard_group_key:
        getCommonValue(items, "wizard_group_key") ||
        `line:${group.source_location_id || group.source_location_name}`,
      safe_machine_name: group.source_location_name,
      machine_name: group.source_location_name,
      process_name: processNames.size > 1 ? `${processNames.size}개 공정` : Array.from(processNames)[0] || "라인 평가",
      process_description: [
        machineNames.size ? `${machineNames.size}대 설비` : "",
        `${items.length}개 유해·위험요인`,
      ].filter(Boolean).join(" · "),
      danger_description: `${items.length}개 유해·위험요인`,
      danger_category: "라인 평가",
      danger_cause: Array.from(processNames).slice(0, 2).join(", "),
      location_names: [group.source_location_name],
      location_summary: group.source_location_name,
      completed_count: completedCount,
      pending_count: pendingCount,
    };
  });
};

const withStepLineRows = (rows = [], step) => {
  if (step !== 1) return rows;
  const lineRows = createStepLineRows(rows);
  if (!lineRows.length) return rows;
  return [...lineRows, ...rows];
};
const getScopeName = (row) =>
  row?.eval_scope === "line"
    ? row?.source_location_name || row?.location_names || "라인"
    : getMachineName(row);

const getHistoryKey = (history = {}) =>
  history.eval_id ||
  [
    history.register_cause_id,
    history.eval_date,
    history.frequency_id,
    history.importancy_id,
    history.standard_id,
  ]
    .filter(Boolean)
    .join("-");

const getHistoryTime = (history = {}) =>
  new Date(history.eval_date || history.approve_date || history.update_date || 0).getTime() || 0;

const getAssetProcessBundleKey = (row = {}) => {
  const registerAssetId = row.register_asset_id ? `asset-${row.register_asset_id}` : "";
  if (registerAssetId) return registerAssetId;

  const machineKey =
    row.machine_id ||
    row.safe_machine_id ||
    row.machine_name ||
    row.safe_machine_name ||
    row.machine_manage_code ||
    "";
  const processKey = row.process_id || row.process_name || row.process_description || "";

  return [machineKey, processKey]
    .map((value) => String(value || "").trim().toLowerCase())
    .join("|");
};

const isSameAssetProcessBundle = (baseRow = {}, targetRow = {}) => {
  const baseKey = getAssetProcessBundleKey(baseRow);
  const targetKey = getAssetProcessBundleKey(targetRow);
  return Boolean(baseKey && targetKey && baseKey === targetKey);
};

const buildLinePreviousHistory = (lineRow, item) => {
  const map = new Map();
  const add = (history, sourceLabel) => {
    if (!history) return;
    const key = getHistoryKey(history);
    if (!key || map.has(key)) return;
    map.set(key, {
      ...history,
      __line_source: sourceLabel || history.__line_source || "",
    });
  };

  (item?.previous_eval_history || []).forEach((history) =>
    add(history, [getMachineName(item), getProcessName(item)].filter(Boolean).join(" / "))
  );

  (lineRow?.items || [])
    .filter((lineItem) => isSameAssetProcessBundle(item, lineItem))
    .forEach((lineItem) => {
      (lineItem?.previous_eval_history || []).forEach((history) =>
        add(history, [getMachineName(lineItem), getProcessName(lineItem)].filter(Boolean).join(" / "))
      );
    });

  return Array.from(map.values())
    .sort((a, b) => getHistoryTime(b) - getHistoryTime(a))
    .slice(0, 8);
};

const toLineDetailRow = (lineRow, item) => {
  if (!lineRow?.__lineGroup) return item;
  const lineName =
    lineRow.source_location_name ||
    lineRow.location_summary ||
    item?.source_location_name ||
    item?.location_summary ||
    "라인";

  return {
    ...item,
    eval_scope: "line",
    source_location_id: lineRow.source_location_id || item?.source_location_id || null,
    source_location_name: lineName,
    location_summary: lineName,
    previous_eval_history: buildLinePreviousHistory(lineRow, item),
    __lineDetail: true,
    __lineGroupKey: lineRow.__lineGroupKey,
  };
};

const getFileViewUrl = (value) => {
  const url = String(value || "").trim();
  if (!url) return "";
  if (/^(https?:|blob:|data:)/i.test(url)) return url;
  const baseURL = String(axiosInstance.defaults.baseURL || "").replace(/\/$/, "");
  if (url.startsWith("/backend/")) {
    return `${baseURL.replace(/\/backend$/, "")}${url}`;
  }
  const path = url.startsWith("/") ? url : `/${url}`;
  return `${baseURL}${path}`;
};

const findEmployeeName = (employees = [], empId) => {
  const targetId = Number(empId);
  if (!targetId) return "-";
  const employee = employees.find((item) => Number(item.emp_id) === targetId);
  if (!employee) return String(empId);
  return [employee.emp_name || employee.emp_id, employee.department_name]
    .filter(Boolean)
    .join(" · ");
};

const getImproveStatusText = (row) => {
  const improveStatus = toNumber(row?.improve_status);
  if (!row?.improve_id && toNumber(row?.requires_improve) !== 1) return "개선 불필요";
  if (improveStatus >= 20) return "완료 처리됨";
  if (improveStatus >= 10 || row?.improve_id) return "개선 후 등록 필요";
  return "대책 작성 필요";
};

const getEvalStatusText = (row) => {
  const evalStatus = toNumber(row?.eval_status);
  if (evalStatus >= 20) return "승인 완료";
  if (evalStatus >= 10) return "완료 대기";
  return "평가 작성 중";
};

const stepConfigs = {
  1: {
    title: "STEP 1 대상 선택/위험성평가",
    label: "대상/위험성평가",
    tone: "#7c3aed",
    icon: <RuleIcon />,
    description: "평가할 대상을 선택하고 가능성·중대성을 입력해 평가를 등록합니다.",
    columns: ["선택", "대상", "위험요인", "가능성", "중대성", "위험성", "상태", "처리"],
  },
  2: {
    title: "STEP 2 개선대책",
    label: "개선대책",
    tone: "#ea580c",
    icon: <TrendingUpIcon />,
    description: "개선 필요 항목에 대책, 담당자, 예정일, 개선 전 사진을 저장합니다.",
    columns: ["대상", "위험요인", "개선대책", "담당자/일정", "개선 전 사진", "저장"],
  },
  3: {
    title: "STEP 3 완료 처리/승인",
    label: "완료 처리/승인",
    tone: "#16a34a",
    icon: <AssignmentTurnedInIcon />,
    description: "개선 후 사진과 완료일을 등록한 뒤 최종 승인합니다.",
    columns: ["대상", "위험요인", "평가", "개선 상태", "승인"],
  },
};

function StatCard({
  icon,
  label,
  value,
  hint,
  color = "#2563eb",
  active = false,
  disabled = false,
  onClick,
}) {
  return (
    <Paper
      elevation={0}
      role={onClick ? "button" : undefined}
      tabIndex={onClick && !disabled ? 0 : undefined}
      onClick={disabled ? undefined : onClick}
      onKeyDown={(event) => {
        if (!onClick || disabled) return;
        if (event.key === "Enter" || event.key === " ") {
          event.preventDefault();
          onClick();
        }
      }}
      sx={{
        position: "relative",
        p: 2,
        border: "1px solid",
        borderColor: active ? color : `${color}26`,
        borderRadius: 2,
        background: active ? `${color}0f` : "linear-gradient(180deg, #fff 0%, #fbfdff 100%)",
        minHeight: 112,
        overflow: "hidden",
        boxShadow: "0 10px 28px rgba(15, 23, 42, 0.04)",
        transition: "border-color 160ms ease, box-shadow 160ms ease, transform 160ms ease",
        cursor: onClick && !disabled ? "pointer" : "default",
        opacity: disabled ? 0.58 : 1,
        "&:hover": {
          borderColor: disabled ? `${color}26` : `${color}55`,
          boxShadow: disabled ? "0 10px 28px rgba(15, 23, 42, 0.04)" : "0 14px 34px rgba(15, 23, 42, 0.07)",
          transform: disabled ? "none" : "translateY(-1px)",
        },
        "&::before": {
          content: '""',
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          height: 3,
          bgcolor: color,
        },
      }}
    >
      <Stack spacing={1.25}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 1,
          }}
        >
          <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 800 }}>
            {label}
          </Typography>
          <Box
            sx={{
              width: 38,
              height: 38,
              borderRadius: "12px",
              display: "grid",
              placeItems: "center",
              color,
              bgcolor: `${color}12`,
              flexShrink: 0,
              "& svg": { fontSize: 22 },
            }}
          >
            {icon}
          </Box>
        </Box>
        <Typography variant="h4" sx={{ fontWeight: 950, lineHeight: 1 }}>
          {value}
        </Typography>
        {hint ? (
          <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700 }}>
            {hint}
          </Typography>
        ) : null}
      </Stack>
    </Paper>
  );
}

function ScopeChip({ row }) {
  const isLine = row?.eval_scope === "line";
  return (
    <Chip
      size="small"
      label={isLine ? "라인" : "설비"}
      color={isLine ? "info" : "default"}
      variant={isLine ? "filled" : "outlined"}
      sx={{ fontWeight: 900, minWidth: 44 }}
    />
  );
}

function StatusChip({ label, color = "default", variant, sx }) {
  return (
    <Chip
      size="small"
      label={label}
      color={color}
      variant={variant || (color === "default" ? "outlined" : "filled")}
      sx={{ fontWeight: 900, ...sx }}
    />
  );
}

function HazardTooltipContent({ row }) {
  const items = row?.__lineGroup ? row.items || [] : [row].filter(Boolean);
  const lineName = row?.source_location_name || row?.location_summary || getScopeName(row);

  if (!row?.__lineGroup) {
    return (
      <Box sx={{ maxWidth: 360 }}>
        <Typography sx={{ color: "#fff", fontSize: 13, fontWeight: 900, lineHeight: 1.55 }}>
          {row?.danger_description || "-"}
        </Typography>
        <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap sx={{ mt: 0.75 }}>
          {[row?.danger_category, row?.danger_cause].filter(Boolean).map((label) => (
            <Box
              key={label}
              sx={{
                px: 0.8,
                py: 0.25,
                borderRadius: 999,
                bgcolor: "rgba(255,255,255,0.12)",
                color: "rgba(255,255,255,0.9)",
                fontSize: 11,
                fontWeight: 800,
              }}
            >
              {label}
            </Box>
          ))}
        </Stack>
      </Box>
    );
  }

  return (
    <Box sx={{ width: 390, maxWidth: "calc(100vw - 48px)" }}>
      <Stack spacing={1}>
        <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
          <Box sx={{ minWidth: 0 }}>
            <Typography sx={{ fontSize: 11, fontWeight: 900, color: "#93c5fd", lineHeight: 1 }}>
              라인 평가
            </Typography>
            <Typography sx={{ mt: 0.45, fontSize: 14, fontWeight: 950, color: "#fff" }} noWrap>
              {lineName}
            </Typography>
          </Box>
          <Box
            sx={{
              px: 1,
              py: 0.45,
              borderRadius: 999,
              bgcolor: "rgba(14,165,233,0.2)",
              color: "#bae6fd",
              border: "1px solid rgba(125,211,252,0.35)",
              fontSize: 12,
              fontWeight: 950,
              flexShrink: 0,
            }}
          >
            {items.length}건
          </Box>
        </Stack>

        <Divider sx={{ borderColor: "rgba(255,255,255,0.12)" }} />

        <Box
          sx={{
            maxHeight: 260,
            overflowY: "auto",
            pr: 0.5,
            "&::-webkit-scrollbar": { width: 6 },
            "&::-webkit-scrollbar-thumb": {
              bgcolor: "rgba(255,255,255,0.22)",
              borderRadius: 999,
            },
          }}
        >
          <Stack spacing={0.75}>
            {items.map((item, index) => (
              <Box
                key={`${item.register_cause_id || item.eval_id || index}`}
                sx={{
                  display: "grid",
                  gridTemplateColumns: "24px minmax(0, 1fr)",
                  gap: 0.9,
                  alignItems: "start",
                  p: 0.85,
                  borderRadius: 1.25,
                  bgcolor: "rgba(255,255,255,0.07)",
                  border: "1px solid rgba(255,255,255,0.08)",
                }}
              >
                <Box
                  sx={{
                    width: 22,
                    height: 22,
                    borderRadius: "50%",
                    display: "grid",
                    placeItems: "center",
                    bgcolor: "rgba(255,255,255,0.12)",
                    color: "#e0f2fe",
                    fontSize: 11,
                    fontWeight: 950,
                  }}
                >
                  {index + 1}
                </Box>
                <Box sx={{ minWidth: 0 }}>
                  <Typography sx={{ color: "#fff", fontSize: 12.5, fontWeight: 850, lineHeight: 1.45 }}>
                    {item.danger_description || "-"}
                  </Typography>
                  <Typography
                    sx={{
                      mt: 0.35,
                      color: "rgba(255,255,255,0.66)",
                      fontSize: 11,
                      fontWeight: 750,
                      lineHeight: 1.35,
                    }}
                    noWrap
                  >
                    {[getMachineName(item), getProcessName(item), item.danger_category, item.danger_cause]
                      .filter((value) => value && value !== "-")
                      .join(" · ")}
                  </Typography>
                </Box>
              </Box>
            ))}
          </Stack>
        </Box>
      </Stack>
    </Box>
  );
}

function MiniSectionTitle({ label, color = "text.secondary" }) {
  return (
    <Typography
      variant="caption"
      color={color}
      sx={{ fontWeight: 950, letterSpacing: 0, lineHeight: 1 }}
    >
      {label}
    </Typography>
  );
}

function EmptyRows({ colSpan, title, body }) {
  return (
    <TableRow>
      <TableCell colSpan={colSpan} sx={{ py: 8, borderBottom: 0 }}>
        <Stack spacing={1} alignItems="center" textAlign="center">
          <Box
            sx={{
              width: 46,
              height: 46,
              borderRadius: "50%",
              display: "grid",
              placeItems: "center",
              color: "text.secondary",
              bgcolor: "grey.100",
            }}
          >
            <PlaylistAddCheckIcon />
          </Box>
          <Typography sx={{ fontWeight: 900 }}>{title}</Typography>
          <Typography variant="body2" color="text.secondary">
            {body}
          </Typography>
        </Stack>
      </TableCell>
    </TableRow>
  );
}

function RowTitle({ row }) {
  return (
    <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 0 }}>
      <ScopeChip row={row} />
      <Box sx={{ minWidth: 0 }}>
        <Typography sx={{ fontWeight: 900 }} noWrap>
          {getScopeName(row)}
        </Typography>
        <Typography variant="caption" color="text.secondary" noWrap>
          {getProcessName(row)}
        </Typography>
      </Box>
    </Stack>
  );
}

function buildSummary(rows, step) {
  const lineCount = rows.filter((row) => row?.eval_scope === "line").length;
  const approved = rows.filter((row) => toNumber(row?.eval_status) >= 20).length;

  if (step === 1) {
    const pending = rows.filter((row) => !row?.eval_id).length;
    const evaluated = rows.filter((row) => row.frequency_id && row.importancy_id).length;
    return [
      { key: "all", count: rows.length, label: "전체 항목", value: `${rows.length}건`, hint: "현재 평가 주기 기준", icon: <Inventory2Icon />, color: "#2563eb" },
      { key: "pending", count: pending, label: "평가 필요", value: `${pending}건`, hint: "가능성·중대성 입력 전", icon: <RuleIcon />, color: "#7c3aed" },
      { key: "evaluated", count: evaluated, label: "평가 완료", value: `${evaluated}건`, hint: "STEP 또는 Wizard 등록", icon: <FactCheckIcon />, color: "#16a34a" },
      { key: "line", count: lineCount, label: "라인 평가", value: `${lineCount}건`, hint: "라인 단위 묶음", icon: <TimelineIcon />, color: "#0891b2" },
    ];
  }

  if (step === 2) {
    const planSaved = rows.filter((row) => row?.improve_id || row?.improve_plan).length;
    const planNeeded = Math.max(rows.length - planSaved, 0);
    return [
      { key: "all", count: rows.length, label: "대책 작성 대상", value: `${rows.length}건`, hint: "STEP 1에서 개선 필요 판정", icon: <TrendingUpIcon />, color: "#ea580c" },
      { key: "missing", count: planNeeded, label: "대책 미작성", value: `${planNeeded}건`, hint: "대책·담당자·예정일 필요", icon: <WarningAmberIcon />, color: "#dc2626" },
      { key: "saved", count: planSaved, label: "대책 저장됨", value: `${planSaved}건`, hint: "STEP 2 입력 완료", icon: <CheckCircleIcon />, color: "#16a34a" },
      { key: "line", count: lineCount, label: "라인 묶음", value: `${lineCount}건`, hint: "라인 평가로 넘어온 항목", icon: <TimelineIcon />, color: "#0891b2" },
    ];
  }

  const completionNeeded = rows.filter((row) => {
    const improvementRequired = toNumber(row?.requires_improve) === 1 || row?.improve_id;
    return improvementRequired && toNumber(row?.improve_status) < 20;
  }).length;

  return [
    { key: "all", count: rows.length, label: "전체 항목", value: `${rows.length}건`, hint: "현재 평가 주기 완료/승인 대상", icon: <Inventory2Icon />, color: "#2563eb" },
    { key: "completion", count: completionNeeded, label: "완료 처리 필요", value: `${completionNeeded}건`, hint: "개선 후 사진·완료일 필요", icon: <WarningAmberIcon />, color: "#ea580c" },
    { key: "approved", count: approved, label: "승인 완료", value: `${approved}건`, hint: "완료 처리와 확인 완료", icon: <CheckCircleIcon />, color: "#16a34a" },
    { key: "line", count: lineCount, label: "라인 묶음", value: `${lineCount}건`, hint: "라인 평가로 넘어온 항목", icon: <TimelineIcon />, color: "#0891b2" },
  ];
}

function Step1Row({
  row,
  selected,
  form,
  forms,
  criteria,
  saving,
  onToggle,
  onFormChange,
  onSave,
  onOpenDetail,
}) {
  const lineItems = row.__lineGroup ? row.items || [] : [];
  const pendingLineItems = row.__lineGroup
    ? lineItems.filter((item) => !isStep1EvaluationComplete(item))
    : [];
  const lineInputCount = row.__lineGroup
    ? pendingLineItems.filter((item) => {
        const itemForm = forms?.[getRowKey(item)] || {};
        return itemForm.frequency_id && itemForm.importancy_id;
      }).length
    : 0;
  const hasEvaluationInput = row.__lineGroup
    ? pendingLineItems.length > 0 && lineInputCount === pendingLineItems.length
    : Boolean(form.frequency_id && form.importancy_id);
  const registered = row.__lineGroup
    ? lineItems.length > 0 && pendingLineItems.length === 0
    : Boolean(row.eval_id);
  const alreadyEvaluated = row.__lineGroup
    ? lineItems.length > 0 && pendingLineItems.length === 0
    : Boolean(row.eval_id && row.frequency_id && row.importancy_id);
  const editable = selected || registered;
  const openDetail = (targetRow) =>
    onOpenDetail?.(row.__lineGroup ? toLineDetailRow(row, targetRow) : targetRow);
  const score = getRiskScore({
    ...row,
    frequency_level:
      criteria.frequencies.find((item) => Number(item.frequency_id) === Number(form.frequency_id))
        ?.frequency_level || row.frequency_level,
    importancy_level:
      criteria.importancies.find((item) => Number(item.importancy_id) === Number(form.importancy_id))
        ?.importancy_level || row.importancy_level,
  });
  const standard = criteria.standards.find(
    (item) =>
      score &&
      score >= toNumber(item.risk_level_min) &&
      score <= toNumber(item.risk_level_max)
  );
  const rowRiskSource =
    !standard && !row?.manage_criteria ? null : standard || { ...row, risk_score: score };
  const saveDisabledReason = alreadyEvaluated
    ? "이미 현재 평가 주기에 위험성평가가 등록된 항목입니다."
    : !editable
    ? "먼저 평가 대상을 선택하세요."
    : row.__lineGroup && pendingLineItems.length > 0 && lineInputCount < pendingLineItems.length
    ? "라인 내 모든 유해·위험요인의 가능성과 중대성을 선택하세요."
    : saving
    ? "등록 중입니다."
    : "";

  if (row.__lineGroup) {
    const lineRowSpan = Math.max(lineItems.length, 1);
    return (
      <>
        {lineItems.map((item, index) => {
          const itemCompleted = isStep1EvaluationComplete(item);
          const itemForm = forms?.[getRowKey(item)] || {};
          const itemEditable = editable && !itemCompleted;
          const itemPreview = getRiskPreview(item, itemForm, criteria);
          const itemStandard = itemPreview.standard;
          const itemRiskSource =
            !itemStandard && !item.manage_criteria
              ? null
              : itemStandard || { ...item, risk_score: itemPreview.score };

          return (
            <TableRow
              key={`${row.__lineGroupKey}-${item.register_cause_id || item.eval_id || index}`}
              hover
              selected={selected || registered}
              onClick={(event) => {
                if (isInteractiveRowClick(event)) return;
                openDetail(item);
              }}
              sx={{ cursor: "pointer" }}
            >
              {index === 0 ? (
                <>
                  <TableCell align="center" rowSpan={lineRowSpan} sx={{ verticalAlign: "middle" }}>
                    <Checkbox
                      checked={selected || registered}
                      disabled={registered}
                      onChange={() => onToggle(row)}
                    />
                  </TableCell>
                  <TableCell rowSpan={lineRowSpan} sx={{ verticalAlign: "middle" }}>
                    <RowTitle row={row} />
                  </TableCell>
                </>
              ) : null}
              <TableCell sx={{ maxWidth: 420 }}>
                <Stack spacing={0.55} sx={{ minWidth: 0 }}>
                  <Stack direction="row" spacing={0.75} alignItems="center" sx={{ minWidth: 0 }}>
                    <Chip
                      size="small"
                      label={itemCompleted ? "완료" : `${index + 1}`}
                      color={itemCompleted ? "success" : "default"}
                      variant={itemCompleted ? "filled" : "outlined"}
                      sx={{ height: 22, fontWeight: 900, flexShrink: 0 }}
                    />
                    <Tooltip title={item.danger_description || ""} placement="top-start">
                      <Typography sx={{ fontWeight: 900 }} noWrap>
                        {item.danger_description || "-"}
                      </Typography>
                    </Tooltip>
                  </Stack>
                  <Stack direction="row" spacing={0.6} flexWrap="wrap" useFlexGap>
                    <StatusChip label={getMachineName(item)} />
                    <StatusChip label={getProcessName(item)} />
                    <StatusChip label={item.danger_category || "-"} />
                    <StatusChip label={item.danger_cause || "-"} />
                  </Stack>
                </Stack>
              </TableCell>
              <TableCell sx={{ minWidth: 170 }}>
                <TextField
                  {...step1SelectInteractionProps}
                  select
                  fullWidth
                  size="small"
                  label="가능성"
                  value={itemForm.frequency_id || item.frequency_id || ""}
                  disabled={!itemEditable}
                  onChange={(event) => onFormChange(item, "frequency_id", event.target.value)}
                >
                  {criteria.frequencies.map((criterion) => (
                    <MenuItem
                      key={criterion.frequency_id}
                      value={criterion.frequency_id}
                      onClick={stopRowClickPropagation}
                    >
                      {criterion.frequency_level} - {criterion.frequency_description}
                    </MenuItem>
                  ))}
                </TextField>
              </TableCell>
              <TableCell sx={{ minWidth: 170 }}>
                <TextField
                  {...step1SelectInteractionProps}
                  select
                  fullWidth
                  size="small"
                  label="중대성"
                  value={itemForm.importancy_id || item.importancy_id || ""}
                  disabled={!itemEditable}
                  onChange={(event) => onFormChange(item, "importancy_id", event.target.value)}
                >
                  {criteria.importancies.map((criterion) => (
                    <MenuItem
                      key={criterion.importancy_id}
                      value={criterion.importancy_id}
                      onClick={stopRowClickPropagation}
                    >
                      {criterion.importancy_level} - {criterion.importancy_description}
                    </MenuItem>
                  ))}
                </TextField>
              </TableCell>
              <TableCell sx={{ minWidth: 160 }}>
                <Stack spacing={0.5}>
                  <Typography sx={{ fontWeight: 900 }}>{itemPreview.score || "-"}점</Typography>
                  <StatusChip
                    label={itemStandard?.manage_criteria || item.manage_criteria || "기준 선택 전"}
                    color="default"
                    variant="filled"
                    sx={getRiskStandardChipSx(itemRiskSource, criteria.standards)}
                  />
                </Stack>
              </TableCell>
              {index === 0 ? (
                <>
                  <TableCell align="center" rowSpan={lineRowSpan} sx={{ verticalAlign: "middle" }}>
                    <StatusChip
                      label={
                        alreadyEvaluated
                          ? "평가 완료"
                          : pendingLineItems.length > 0 && hasEvaluationInput
                          ? "등록 필요"
                          : pendingLineItems.length > 0
                          ? `${lineInputCount}/${pendingLineItems.length} 입력`
                          : "평가 완료"
                      }
                      color={alreadyEvaluated ? "success" : selected || registered ? "info" : "default"}
                    />
                  </TableCell>
                  <TableCell align="center" rowSpan={lineRowSpan} sx={{ verticalAlign: "middle" }}>
                    <Tooltip title={saveDisabledReason} disableHoverListener={!saveDisabledReason}>
                      <span>
                        <Button
                          size="small"
                          variant="contained"
                          startIcon={<SaveIcon />}
                          disabled={!editable || alreadyEvaluated || saving || !hasEvaluationInput}
                          onClick={() => onSave(row)}
                          sx={{ fontWeight: 900, whiteSpace: "nowrap" }}
                        >
                          {saving ? "등록 중" : "평가 등록"}
                        </Button>
                      </span>
                    </Tooltip>
                  </TableCell>
                </>
              ) : null}
            </TableRow>
          );
        })}
      </>
    );
  }

  return (
    <TableRow
      hover
      selected={selected || registered}
      onClick={(event) => {
        if (isInteractiveRowClick(event)) return;
        openDetail(row);
      }}
      sx={{ cursor: "pointer" }}
    >
      <TableCell align="center">
        <Checkbox
          checked={selected || registered}
          disabled={registered}
          onChange={() => onToggle(row)}
        />
      </TableCell>
      <TableCell>
        <RowTitle row={row} />
      </TableCell>
      <TableCell sx={{ maxWidth: 340 }}>
        <Tooltip
          title={<HazardTooltipContent row={row} />}
          placement="top-start"
          arrow
          enterDelay={250}
          slotProps={{
            tooltip: {
              sx: {
                p: 1.1,
                bgcolor: "#0f172a",
                borderRadius: 2,
                boxShadow: "0 18px 44px rgba(15, 23, 42, 0.32)",
                border: "1px solid rgba(255,255,255,0.1)",
                maxWidth: "none",
              },
            },
            arrow: {
              sx: { color: "#0f172a" },
            },
          }}
        >
          <Typography noWrap sx={{ fontWeight: 800 }}>
            {row?.danger_description || "-"}
          </Typography>
        </Tooltip>
        <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap sx={{ mt: 0.6 }}>
          <StatusChip label={row?.danger_category || "-"} />
          <StatusChip label={row?.danger_cause || "-"} />
        </Stack>
      </TableCell>
      <TableCell sx={{ minWidth: 170 }}>
        <TextField
          {...step1SelectInteractionProps}
          select
          fullWidth
          size="small"
          label="가능성"
          value={form.frequency_id || ""}
          disabled={!editable || alreadyEvaluated}
          onChange={(event) => onFormChange(row, "frequency_id", event.target.value)}
        >
          {criteria.frequencies.map((item) => (
            <MenuItem
              key={item.frequency_id}
              value={item.frequency_id}
              onClick={stopRowClickPropagation}
            >
              {item.frequency_level} - {item.frequency_description}
            </MenuItem>
          ))}
        </TextField>
      </TableCell>
      <TableCell sx={{ minWidth: 170 }}>
        <TextField
          {...step1SelectInteractionProps}
          select
          fullWidth
          size="small"
          label="중대성"
          value={form.importancy_id || ""}
          disabled={!editable || alreadyEvaluated}
          onChange={(event) => onFormChange(row, "importancy_id", event.target.value)}
        >
          {criteria.importancies.map((item) => (
            <MenuItem
              key={item.importancy_id}
              value={item.importancy_id}
              onClick={stopRowClickPropagation}
            >
              {item.importancy_level} - {item.importancy_description}
            </MenuItem>
          ))}
        </TextField>
      </TableCell>
      <TableCell>
        <Stack spacing={0.5}>
          <Typography sx={{ fontWeight: 900 }}>{score || "-"}점</Typography>
          <StatusChip
            label={standard?.manage_criteria || row?.manage_criteria || "기준 선택 전"}
            color="default"
            variant="filled"
            sx={getRiskStandardChipSx(rowRiskSource, criteria.standards)}
          />
        </Stack>
      </TableCell>
      <TableCell align="center">
        <StatusChip
          label={
            alreadyEvaluated
              ? "평가 완료"
              : row.__lineGroup && pendingLineItems.length > 0 && hasEvaluationInput
              ? "등록 필요"
              : row.__lineGroup && pendingLineItems.length > 0
              ? `${pendingLineItems.length}건 평가 필요`
              : registered
              ? "평가 완료"
              : selected
              ? "평가 입력"
              : "선택 필요"
          }
          color={
            alreadyEvaluated
              ? "success"
              : selected || registered || (row.__lineGroup && pendingLineItems.length > 0)
              ? "info"
              : "default"
          }
        />
      </TableCell>
      <TableCell align="center">
        <Tooltip title={saveDisabledReason} disableHoverListener={!saveDisabledReason}>
          <span>
            <Button
              size="small"
              variant="contained"
              startIcon={<SaveIcon />}
              disabled={!editable || alreadyEvaluated || saving}
              onClick={() => onSave(row)}
              sx={{ fontWeight: 900, whiteSpace: "nowrap" }}
            >
              {saving ? "등록 중" : "평가 등록"}
            </Button>
          </span>
        </Tooltip>
      </TableCell>
    </TableRow>
  );
}

function Step1Card({
  row,
  selected,
  form,
  forms,
  criteria,
  saving,
  onToggle,
  onFormChange,
  onSave,
  onOpenDetail,
}) {
  const lineItems = row.__lineGroup ? row.items || [] : [];
  const pendingLineItems = row.__lineGroup
    ? lineItems.filter((item) => !isStep1EvaluationComplete(item))
    : [];
  const lineInputCount = row.__lineGroup
    ? pendingLineItems.filter((item) => {
        const itemForm = forms?.[getRowKey(item)] || {};
        return itemForm.frequency_id && itemForm.importancy_id;
      }).length
    : 0;
  const hasEvaluationInput = row.__lineGroup
    ? pendingLineItems.length > 0 && lineInputCount === pendingLineItems.length
    : Boolean(form.frequency_id && form.importancy_id);
  const registered = row.__lineGroup
    ? lineItems.length > 0 && pendingLineItems.length === 0
    : Boolean(row.eval_id);
  const alreadyEvaluated = row.__lineGroup
    ? lineItems.length > 0 && pendingLineItems.length === 0
    : Boolean(row.eval_id && row.frequency_id && row.importancy_id);
  const editable = selected || registered;
  const openDetail = (targetRow) =>
    onOpenDetail?.(row.__lineGroup ? toLineDetailRow(row, targetRow) : targetRow);
  const saveDisabledReason = alreadyEvaluated
    ? "이미 현재 평가 주기에 위험성평가가 등록된 항목입니다."
    : !editable
    ? "먼저 평가 대상을 선택하세요."
    : row.__lineGroup && pendingLineItems.length > 0 && lineInputCount < pendingLineItems.length
    ? "라인 내 모든 유해·위험요인의 가능성과 중대성을 선택하세요."
    : !hasEvaluationInput
    ? "가능성과 중대성을 선택하세요."
    : saving
    ? "등록 중입니다."
    : "";

  const renderRiskPreview = (targetRow, targetForm = {}) => {
    const preview = getRiskPreview(targetRow, targetForm, criteria);
    const standard = preview.standard;
    const neutral = !standard && !targetRow?.manage_criteria;
    const riskSource = neutral
      ? null
      : standard || {
          ...targetRow,
          risk_score: preview.score,
        };
    const riskStyle = getRiskStandardStyle(riskSource, criteria.standards);
    const boxSx = getRiskStandardBoxSx(riskSource, criteria.standards);

    return (
      <Box
        sx={{
          p: 1,
          borderRadius: 1.5,
          bgcolor: boxSx.bgcolor,
          border: "1px solid",
          borderColor: boxSx.borderColor,
          minWidth: 0,
        }}
      >
        <Stack direction="row" spacing={0.75} alignItems="center" justifyContent="space-between">
          <Typography sx={{ fontWeight: 950, color: neutral ? "text.secondary" : riskStyle.fg }}>
            {preview.score || "-"}점
          </Typography>
          <StatusChip
            label={standard?.manage_criteria || targetRow?.manage_criteria || "기준 선택 전"}
            color="default"
            variant="filled"
            sx={getRiskStandardChipSx(riskSource, criteria.standards)}
          />
        </Stack>
      </Box>
    );
  };

  const renderSelects = (targetRow, targetForm = {}, disabled = false) => (
    <Box
      onClick={stopRowClickPropagation}
      onMouseDown={stopRowClickPropagation}
      onTouchStart={stopRowClickPropagation}
      sx={{
        display: "grid",
        gridTemplateColumns: { xs: "1fr", sm: "repeat(2, minmax(0, 1fr))" },
        gap: 1,
      }}
    >
      <TextField
        select
        fullWidth
        size="small"
        label="가능성"
        value={targetForm.frequency_id || targetRow.frequency_id || ""}
        disabled={disabled}
        onChange={(event) => onFormChange(targetRow, "frequency_id", event.target.value)}
        sx={{ "& .MuiInputBase-root": { bgcolor: "#fff" } }}
      >
        {criteria.frequencies.map((criterion) => (
          <MenuItem key={criterion.frequency_id} value={criterion.frequency_id}>
            {criterion.frequency_level} - {criterion.frequency_description}
          </MenuItem>
        ))}
      </TextField>
      <TextField
        select
        fullWidth
        size="small"
        label="중대성"
        value={targetForm.importancy_id || targetRow.importancy_id || ""}
        disabled={disabled}
        onChange={(event) => onFormChange(targetRow, "importancy_id", event.target.value)}
        sx={{ "& .MuiInputBase-root": { bgcolor: "#fff" } }}
      >
        {criteria.importancies.map((criterion) => (
          <MenuItem key={criterion.importancy_id} value={criterion.importancy_id}>
            {criterion.importancy_level} - {criterion.importancy_description}
          </MenuItem>
        ))}
      </TextField>
    </Box>
  );

  if (row.__lineGroup) {
    return (
      <Paper
        elevation={0}
        sx={{
          p: { xs: 1.5, sm: 2 },
          border: "1.5px solid",
          borderColor: selected || registered ? "primary.light" : "divider",
          borderRadius: 2,
          bgcolor: selected || registered ? "#f8fbff" : "#fff",
          boxShadow: "0 12px 30px rgba(15, 23, 42, 0.05)",
        }}
      >
        <Stack spacing={1.5}>
          <Stack direction="row" spacing={1} alignItems="flex-start" justifyContent="space-between">
            <Stack direction="row" spacing={1} alignItems="flex-start" sx={{ minWidth: 0 }}>
              <Checkbox
                checked={selected || registered}
                disabled={registered}
                onChange={() => onToggle(row)}
                sx={{ p: 0.25, mt: 0.25 }}
              />
              <Stack spacing={0.5} sx={{ minWidth: 0 }}>
                <RowTitle row={row} />
                <Stack direction="row" spacing={0.6} flexWrap="wrap" useFlexGap>
                  <StatusChip label={`${lineItems.length}개 위험요인`} color="info" />
                  <StatusChip label={`${lineInputCount}/${pendingLineItems.length || lineItems.length} 입력`} color={hasEvaluationInput || alreadyEvaluated ? "success" : "default"} />
                </Stack>
              </Stack>
            </Stack>
            <StatusChip
              label={alreadyEvaluated ? "평가 완료" : selected ? "평가 입력" : "선택 필요"}
              color={alreadyEvaluated ? "success" : selected ? "info" : "default"}
            />
          </Stack>

          <Stack spacing={1}>
            {lineItems.map((item, index) => {
              const itemCompleted = isStep1EvaluationComplete(item);
              const itemForm = forms?.[getRowKey(item)] || {};
              const itemEditable = editable && !itemCompleted;

              return (
                <Paper
                  key={`${row.__lineGroupKey}-card-${item.register_cause_id || item.eval_id || index}`}
                  elevation={0}
                  onClick={(event) => {
                    if (isInteractiveRowClick(event)) return;
                    openDetail(item);
                  }}
                  sx={{
                    p: 1.25,
                    border: "1px solid",
                    borderColor: itemCompleted ? "#bbf7d0" : "divider",
                    borderRadius: 1.75,
                    bgcolor: itemCompleted ? "#f0fdf4" : "#fff",
                    cursor: "pointer",
                  }}
                >
                  <Stack spacing={1}>
                    <Stack direction="row" spacing={0.75} alignItems="flex-start">
                      <Chip
                        size="small"
                        label={itemCompleted ? "완료" : `${index + 1}`}
                        color={itemCompleted ? "success" : "default"}
                        variant={itemCompleted ? "filled" : "outlined"}
                        sx={{ height: 22, fontWeight: 900, flexShrink: 0 }}
                      />
                      <Box sx={{ minWidth: 0, flex: 1 }}>
                        <Typography sx={{ fontWeight: 900, lineHeight: 1.35 }}>
                          {item.danger_description || "-"}
                        </Typography>
                        <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap sx={{ mt: 0.6 }}>
                          <StatusChip label={getMachineName(item)} />
                          <StatusChip label={getProcessName(item)} />
                          <StatusChip label={item.danger_category || "-"} />
                          <StatusChip label={item.danger_cause || "-"} />
                        </Stack>
                      </Box>
                    </Stack>
                    {renderSelects(item, itemForm, !itemEditable)}
                    {renderRiskPreview(item, itemForm)}
                  </Stack>
                </Paper>
              );
            })}
          </Stack>

          <Tooltip title={saveDisabledReason} disableHoverListener={!saveDisabledReason}>
            <span>
              <Button
                fullWidth
                variant="contained"
                startIcon={<SaveIcon />}
                disabled={!editable || alreadyEvaluated || saving || !hasEvaluationInput}
                onClick={() => onSave(row)}
                sx={{ fontWeight: 900, py: 1.05 }}
              >
                {saving ? "등록 중" : "평가 등록"}
              </Button>
            </span>
          </Tooltip>
        </Stack>
      </Paper>
    );
  }

  return (
    <Paper
      elevation={0}
      onClick={(event) => {
        if (isInteractiveRowClick(event)) return;
        openDetail(row);
      }}
      sx={{
        p: { xs: 1.5, sm: 2 },
        border: "1.5px solid",
        borderColor: selected || registered ? "primary.light" : "divider",
        borderRadius: 2,
        bgcolor: selected || registered ? "#f8fbff" : "#fff",
        boxShadow: "0 12px 30px rgba(15, 23, 42, 0.05)",
        cursor: "pointer",
      }}
    >
      <Stack spacing={1.4}>
        <Stack direction="row" spacing={1} alignItems="flex-start" justifyContent="space-between">
          <Stack direction="row" spacing={1} alignItems="flex-start" sx={{ minWidth: 0 }}>
            <Checkbox
              checked={selected || registered}
              disabled={registered}
              onChange={() => onToggle(row)}
              sx={{ p: 0.25, mt: 0.25 }}
            />
            <RowTitle row={row} />
          </Stack>
          <StatusChip
            label={alreadyEvaluated ? "평가 완료" : selected ? "평가 입력" : "선택 필요"}
            color={alreadyEvaluated ? "success" : selected ? "info" : "default"}
          />
        </Stack>

        <Box>
          <Typography sx={{ fontWeight: 900, lineHeight: 1.4 }}>
            {row?.danger_description || "-"}
          </Typography>
          <Stack direction="row" spacing={0.6} flexWrap="wrap" useFlexGap sx={{ mt: 0.65 }}>
            <StatusChip label={row?.danger_category || "-"} />
            <StatusChip label={row?.danger_cause || "-"} />
          </Stack>
        </Box>

        {renderSelects(row, form, !editable || alreadyEvaluated)}
        {renderRiskPreview(row, form)}

        <Tooltip title={saveDisabledReason} disableHoverListener={!saveDisabledReason}>
          <span>
            <Button
              fullWidth
              variant="contained"
              startIcon={<SaveIcon />}
              disabled={!editable || alreadyEvaluated || saving || !hasEvaluationInput}
              onClick={() => onSave(row)}
              sx={{ fontWeight: 900, py: 1.05 }}
            >
              {saving ? "등록 중" : "평가 등록"}
            </Button>
          </span>
        </Tooltip>
      </Stack>
    </Paper>
  );
}

function Step2Row({ row, form, criteria, saving, onFormChange, onSave }) {
  const score = getRiskScore({
    ...row,
    frequency_level:
      criteria.frequencies.find((item) => Number(item.frequency_id) === Number(form.frequency_id))
        ?.frequency_level || row.frequency_level,
    importancy_level:
      criteria.importancies.find((item) => Number(item.importancy_id) === Number(form.importancy_id))
        ?.importancy_level || row.importancy_level,
  });

  const standard = criteria.standards.find(
    (item) =>
      score &&
      score >= toNumber(item.risk_level_min) &&
      score <= toNumber(item.risk_level_max)
  );
  const saveDisabledReason = saving ? "저장 중입니다." : "";

  const step2RiskSource =
    !standard && !row?.manage_criteria ? null : standard || { ...row, risk_score: score };

  return (
    <TableRow hover>
      <TableCell>
        <RowTitle row={row} />
      </TableCell>
      <TableCell sx={{ maxWidth: 360 }}>
        <Tooltip title={row?.danger_description || ""} placement="top-start">
          <Typography noWrap>{row?.danger_description || "-"}</Typography>
        </Tooltip>
      </TableCell>
      <TableCell sx={{ minWidth: 170 }}>
        <TextField
          select
          fullWidth
          size="small"
          label="가능성"
          value={form.frequency_id || ""}
          onChange={(event) => onFormChange(row, "frequency_id", event.target.value)}
        >
          {criteria.frequencies.map((item) => (
            <MenuItem key={item.frequency_id} value={item.frequency_id}>
              {item.frequency_level} - {item.frequency_description}
            </MenuItem>
          ))}
        </TextField>
      </TableCell>
      <TableCell sx={{ minWidth: 170 }}>
        <TextField
          select
          fullWidth
          size="small"
          label="중대성"
          value={form.importancy_id || ""}
          onChange={(event) => onFormChange(row, "importancy_id", event.target.value)}
        >
          {criteria.importancies.map((item) => (
            <MenuItem key={item.importancy_id} value={item.importancy_id}>
              {item.importancy_level} - {item.importancy_description}
            </MenuItem>
          ))}
        </TextField>
      </TableCell>
      <TableCell>
        <Stack spacing={0.5}>
          <Typography sx={{ fontWeight: 900 }}>{score || "-"}점</Typography>
          <StatusChip
            label={standard?.manage_criteria || row?.manage_criteria || "기준 선택 전"}
            color="default"
            variant="filled"
            sx={getRiskStandardChipSx(step2RiskSource, criteria.standards)}
          />
        </Stack>
      </TableCell>
      <TableCell>
        {toNumber(standard?.requires_improve ?? row?.requires_improve) === 1
          ? "개선 필요"
          : "허용"}
      </TableCell>
      <TableCell align="center">
        <Tooltip title={saveDisabledReason} disableHoverListener={!saveDisabledReason}>
          <span>
            <Button
              size="small"
              variant="contained"
              startIcon={<SaveIcon />}
              disabled={saving}
              onClick={() => onSave(row)}
              sx={{ fontWeight: 900, whiteSpace: "nowrap" }}
            >
              {saving ? "저장 중" : "저장"}
            </Button>
          </span>
        </Tooltip>
      </TableCell>
    </TableRow>
  );
}

function Step3Row({ row, form, employees, saving, onFormChange, onUpload, onSave }) {
  const saveDisabledReason = saving ? "저장 중입니다." : "";
  const planSaved = Boolean(row?.improve_id || row?.improve_plan);
  const planEditing = Boolean(!planSaved && form?.improve_plan);
  const actionLabel = planSaved ? "개선대책 수정" : "개선대책 등록";
  const riskScore = getRiskScore(row);
  const riskSource = { ...row, risk_score: riskScore };
  const beforePreviewUrl =
    form?.improve_before_preview ||
    form?.improve_before ||
    row?.improve_before ||
    "";

  return (
    <Paper
      elevation={0}
      sx={{
        p: { xs: 1.5, md: 2 },
        border: "1px solid",
        borderColor: planSaved ? "primary.light" : "divider",
        borderRadius: 2,
        bgcolor: "#fff",
        boxShadow: "0 10px 26px rgba(15, 23, 42, 0.04)",
        transition: "border-color 160ms ease, box-shadow 160ms ease, transform 160ms ease",
        "&:hover": {
          borderColor: "primary.main",
          boxShadow: "0 14px 34px rgba(15, 23, 42, 0.07)",
          transform: "translateY(-1px)",
        },
      }}
    >
      <Stack spacing={1.5}>
        <Stack
          direction={{ xs: "column", md: "row" }}
          spacing={1.25}
          alignItems={{ xs: "stretch", md: "flex-start" }}
          justifyContent="space-between"
        >
          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={1.25}
            alignItems={{ xs: "flex-start", sm: "center" }}
            sx={{ minWidth: 0 }}
          >
            <RowTitle row={row} />
            <Divider flexItem orientation="vertical" sx={{ display: { xs: "none", sm: "block" } }} />
            <Box sx={{ minWidth: 0, width: "100%" }}>
              <Tooltip title={row?.danger_description || ""} placement="top-start">
                <Typography
                  sx={{
                    fontWeight: 900,
                    lineHeight: 1.35,
                    display: "-webkit-box",
                    WebkitBoxOrient: "vertical",
                    WebkitLineClamp: { xs: 2, sm: 1 },
                    overflow: "hidden",
                    wordBreak: "keep-all",
                    overflowWrap: "anywhere",
                  }}
                >
                  {row?.danger_description || "-"}
                </Typography>
              </Tooltip>
              <Stack
                direction={{ xs: "column", sm: "row" }}
                spacing={0.75}
                alignItems={{ xs: "stretch", sm: "center" }}
                sx={{
                  mt: 0.6,
                  minWidth: 0,
                  maxWidth: "100%",
                }}
              >
                <Chip
                  size="small"
                  label={`위험성 ${riskScore || "-"}점`}
                  sx={{
                    ...getRiskStandardChipSx(riskSource),
                    alignSelf: { xs: "flex-start", sm: "center" },
                    maxWidth: "100%",
                    minHeight: 26,
                    "& .MuiChip-label": {
                      minWidth: 0,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      px: 1,
                    },
                  }}
                />
                <StatusChip
                  label={row?.manage_criteria || "-"}
                  color="default"
                  variant="filled"
                  sx={{
                    ...getRiskStandardChipSx(riskSource),
                    alignSelf: { xs: "stretch", sm: "center" },
                    width: { xs: "100%", sm: "auto" },
                    maxWidth: "100%",
                    height: "auto",
                    minHeight: 28,
                    alignItems: "center",
                    justifyContent: "center",
                    "& .MuiChip-label": {
                      display: "block",
                      width: "100%",
                      whiteSpace: "normal",
                      overflow: "visible",
                      textOverflow: "clip",
                      textAlign: "center",
                      lineHeight: 1.28,
                      py: 0.45,
                      px: 1,
                      wordBreak: "keep-all",
                      overflowWrap: "anywhere",
                    },
                  }}
                />
              </Stack>
            </Box>
          </Stack>

          <Tooltip title={saveDisabledReason} disableHoverListener={!saveDisabledReason}>
            <span>
              <Button
                size="small"
                variant="contained"
                startIcon={<SaveIcon />}
                disabled={saving}
                onClick={() => onSave(row)}
                sx={{ fontWeight: 900, whiteSpace: "nowrap", alignSelf: { xs: "stretch", md: "flex-start" } }}
              >
                {saving ? "저장 중" : actionLabel}
              </Button>
            </span>
          </Tooltip>
        </Stack>

        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: {
              xs: "1fr",
              md: "minmax(340px, 1.15fr) minmax(260px, 0.85fr)",
              xl: "minmax(440px, 1.3fr) minmax(300px, 0.9fr) minmax(260px, 0.75fr)",
            },
            gap: 1.5,
            alignItems: "stretch",
          }}
        >
          <Paper
            elevation={0}
            sx={{
              p: 1.5,
              border: "1px solid",
              borderColor: "divider",
              borderRadius: 1.5,
              bgcolor: "#fff",
              gridColumn: { md: "span 2", xl: "auto" },
            }}
          >
            <Stack spacing={1.25}>
              <MiniSectionTitle label="개선대책" />
              <TextField
                fullWidth
                multiline
                minRows={3}
                size="small"
                placeholder="개선대책을 입력하세요."
                value={form.improve_plan || ""}
                onChange={(event) => onFormChange(row, "improve_plan", event.target.value)}
                sx={{ "& .MuiInputBase-root": { bgcolor: "#fff", alignItems: "flex-start" } }}
              />
            </Stack>
          </Paper>

          <Paper
            elevation={0}
            sx={{
              p: 1.5,
              border: "1px solid",
              borderColor: "divider",
              borderRadius: 1.5,
              bgcolor: "#f8fafc",
            }}
          >
            <Stack spacing={1.75}>
              <MiniSectionTitle label="담당/일정" />
              <EmployeeSelect
                employees={employees}
                value={form.emp_id || ""}
                onChange={(value) => onFormChange(row, "emp_id", value)}
                label="담당자"
                required
                helperText="팀을 먼저 선택하면 담당자를 빠르게 찾을 수 있습니다."
              />
              <TextField
                fullWidth
                size="small"
                type="date"
                label="개선 예정일"
                value={form.expected_date || ""}
                onChange={(event) => onFormChange(row, "expected_date", event.target.value)}
                InputLabelProps={{ shrink: true }}
                sx={{ "& .MuiInputBase-root": { bgcolor: "#fff", minHeight: 42 } }}
              />
            </Stack>
          </Paper>

          <Paper
            elevation={0}
            sx={{
              p: 1.5,
              border: "1px solid",
              borderColor: planSaved ? "primary.light" : "divider",
              borderRadius: 1.5,
              bgcolor: "#f8fafc",
              gridColumn: { md: "span 2", xl: "auto" },
            }}
          >
            <Stack spacing={1.25}>
              <Stack
                direction="row"
                spacing={1}
                alignItems="center"
                justifyContent="space-between"
                flexWrap="wrap"
                useFlexGap
              >
                <MiniSectionTitle label="개선 전 사진" color="text.secondary" />
                <StatusChip
                  label={planSaved ? "대책 저장됨" : planEditing ? "입력 중" : "대책 미작성"}
                  color={planSaved ? "info" : planEditing ? "default" : "warning"}
                />
              </Stack>
              <EvidencePreview
                compact
                hideLabel
                label="개선 전 사진"
                value={beforePreviewUrl}
                emptyText="사진 미리보기 없음"
                imageHeight={128}
              />
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap alignItems="center">
                <Button component="label" size="small" variant="outlined" startIcon={<UploadFileIcon />} sx={{ fontWeight: 900 }}>
                  {beforePreviewUrl ? "사진 변경" : "사진 첨부"}
                  <input
                    hidden
                    type="file"
                    accept="image/*"
                    onChange={(event) => onUpload(row, "improve_before", event.target.files?.[0])}
                  />
                </Button>
              </Stack>
              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 800 }}>
                {beforePreviewUrl
                  ? "저장 전에도 선택한 사진을 확인할 수 있습니다."
                  : "사진은 선택 입력입니다."}
              </Typography>
            </Stack>
          </Paper>
        </Box>
      </Stack>
    </Paper>
  );
}

function DetailField({ label, value }) {
  return (
    <Box>
      <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 800 }}>
        {label}
      </Typography>
      <Typography variant="body2" sx={{ fontWeight: 900, mt: 0.25 }}>
        {value || "-"}
      </Typography>
    </Box>
  );
}

function RiskHistoryDialog({ open, row, criteria, onClose }) {
  const previousHistory = (Array.isArray(row?.previous_eval_history)
    ? [...row.previous_eval_history]
    : []
  ).sort(
    (a, b) =>
      getHistoryTime(b) - getHistoryTime(a) ||
      toNumber(b.eval_id) - toNumber(a.eval_id)
  );

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle sx={{ pr: 6 }}>
        <Stack spacing={0.5}>
          <Typography variant="h6" sx={{ fontWeight: 950 }}>
            위험요인 상세
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {row ? `${getScopeName(row)} · ${getProcessName(row)}` : "-"}
          </Typography>
        </Stack>
        <IconButton
          aria-label="닫기"
          onClick={onClose}
          sx={{ position: "absolute", right: 12, top: 12 }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers sx={{ bgcolor: "#f8fafc" }}>
        <Stack spacing={1.5}>
          <Paper elevation={0} sx={{ p: 2, border: "1px solid", borderColor: "divider", borderRadius: 2 }}>
            <Stack spacing={1}>
              <Typography sx={{ fontWeight: 950 }}>{row?.danger_description || "-"}</Typography>
              <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
                <StatusChip label={row?.danger_category || "위험분류 미지정"} />
                <StatusChip label={row?.danger_cause || "위험원인 미지정"} />
                <StatusChip label={row?.eval_scope === "line" ? "라인 평가" : "설비 평가"} color="info" />
                <StatusChip label={getEvalStatusText(row)} color={toNumber(row?.eval_status) >= 20 ? "success" : "default"} />
              </Stack>
            </Stack>
          </Paper>

          <Paper elevation={0} sx={{ p: 2, border: "1px solid", borderColor: "divider", borderRadius: 2 }}>
            <Stack spacing={1.25}>
              <MiniSectionTitle label="이전 평가이력" color="primary.main" />
              {previousHistory.length ? (
                <Stack spacing={0.75}>
                  {previousHistory.map((history) => {
                    const afterRiskScore = toNumber(history.improve_after_risk);
                    const improvementCompleted =
                      toNumber(history.improve_status) >= 20 || Boolean(history.comp_date);

                    return (
                      <Box
                      key={history.eval_id}
                      sx={{
                        p: 1.25,
                        border: "1px solid",
                        borderColor: "divider",
                        borderRadius: 1.5,
                        bgcolor: "#fff",
                        display: "grid",
                        gridTemplateColumns: { xs: "1fr", md: "120px 1fr 160px" },
                        gap: 1,
                        alignItems: "center",
                      }}
                    >
                      <Typography sx={{ fontWeight: 950 }}>{history.eval_date || "-"}</Typography>
                      <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
                        {history.__line_source ? <StatusChip label={history.__line_source} color="default" /> : null}
                        <StatusChip label={history.eval_scope === "line" ? "라인 평가" : "설비 평가"} color="info" />
                        <StatusChip label={`가능성 ${history.frequency_level || "-"}`} color="default" />
                        <StatusChip label={`중대성 ${history.importancy_level || "-"}`} color="default" />
                        <StatusChip
                          label={history.manage_criteria || "관리 기준 없음"}
                          color="default"
                          variant="filled"
                          sx={getRiskStandardChipSx(history, criteria?.standards || [])}
                        />
                      </Stack>
                      <Stack spacing={0.5} alignItems={{ xs: "flex-start", md: "flex-end" }}>
                        <Typography sx={{ fontWeight: 950, textAlign: { xs: "left", md: "right" } }}>
                          {history.risk_score ? `개선 전 ${history.risk_score}점` : "-"}
                        </Typography>
                        {improvementCompleted ? (
                          afterRiskScore ? (
                            <StatusChip
                              label={`개선 후 ${afterRiskScore}점`}
                              color="default"
                              variant="filled"
                              sx={getRiskStandardChipSx(afterRiskScore, criteria?.standards || [])}
                            />
                          ) : (
                            <StatusChip label="개선 후 점수 미등록" color="default" />
                          )
                        ) : (
                          <StatusChip label="개선 미등록" color="default" />
                        )}
                      </Stack>
                      </Box>
                    );
                  })}
                </Stack>
              ) : (
                <Box
                  sx={{
                    p: 1.5,
                    border: "1px dashed",
                    borderColor: "divider",
                    borderRadius: 1.5,
                    bgcolor: "#f8fafc",
                    color: "text.secondary",
                  }}
                >
                  <Typography variant="body2" sx={{ fontWeight: 800 }}>
                    최근 3개월간 등록된 평가이력이 없습니다.
                  </Typography>
                </Box>
              )}
            </Stack>
          </Paper>

        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} sx={{ fontWeight: 900 }}>
          닫기
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function EvidencePreview({
  label,
  value,
  emptyText = "사진 없음",
  required = false,
  compact = false,
  hideLabel = false,
  imageHeight,
}) {
  const src = getFileViewUrl(value);
  const previewHeight = imageHeight || (compact ? 52 : 190);
  return (
    <Paper
      elevation={0}
      sx={{
        p: hideLabel ? 0 : compact ? 0.5 : 1,
        border: hideLabel ? 0 : "1px solid",
        borderColor: hideLabel ? "transparent" : src ? "divider" : required ? "warning.light" : "divider",
        borderRadius: 1.5,
        bgcolor: src ? "#fff" : required ? "#fffaf0" : "#f8fafc",
        minWidth: compact ? 82 : 0,
        overflow: "hidden",
      }}
    >
      <Stack spacing={0.75}>
        {!hideLabel ? (
          <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 900 }}>
            {label}
          </Typography>
        ) : null}
        {src ? (
          <Box
            component="img"
            src={src}
            alt={label}
            sx={{
              width: "100%",
              height: previewHeight,
              objectFit: "cover",
              borderRadius: hideLabel ? 1.35 : 1,
              border: hideLabel ? 0 : "1px solid rgba(148, 163, 184, 0.24)",
              bgcolor: "#f8fafc",
              display: "block",
            }}
          />
        ) : (
          <Stack
            spacing={0.5}
            alignItems="center"
            justifyContent="center"
            sx={{
              height: previewHeight,
              borderRadius: 1,
              border: hideLabel ? 0 : "1px dashed",
              borderColor: hideLabel ? "transparent" : required ? "warning.main" : "divider",
              color: required ? "warning.dark" : "text.secondary",
              textAlign: "center",
            }}
          >
            <ImageIcon fontSize={compact ? "small" : "medium"} />
            <Typography variant="caption" sx={{ fontWeight: 900 }}>
              {emptyText}
            </Typography>
          </Stack>
        )}
      </Stack>
    </Paper>
  );
}

function ImprovementDetailDialog({
  open,
  onClose,
  row,
  form,
  employees,
  improvementDone,
  improvementRequired,
}) {
  const beforeUrl =
    form?.improve_before_preview ||
    form?.improve_before ||
    row?.improve_before ||
    "";
  const afterUrl =
    form?.improve_after_preview ||
    form?.improve_after ||
    row?.improve_after ||
    "";
  const assignee = form?.emp_id || row?.emp_id || "";
  const afterRisk = form?.improve_after_risk || row?.improve_after_risk || "";
  const completeDate = form?.comp_date || row?.comp_date || "";
  const completionNote = form?.completion_note || row?.completion_note || "";

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle sx={{ pr: 6 }}>
        <Stack spacing={0.5}>
          <Typography variant="h6" sx={{ fontWeight: 950 }}>
            개선대책 상세
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {getScopeName(row)} · {getProcessName(row)}
          </Typography>
        </Stack>
        <IconButton
          aria-label="닫기"
          onClick={onClose}
          sx={{ position: "absolute", right: 12, top: 12 }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers sx={{ bgcolor: "#f8fafc" }}>
        <Stack spacing={2}>
          <Paper elevation={0} sx={{ p: 2, border: "1px solid", borderColor: "divider", borderRadius: 2 }}>
            <Stack spacing={1}>
              <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
                <StatusChip label={getEvalStatusText(row)} color={toNumber(row?.eval_status) >= 20 ? "success" : "default"} />
                <StatusChip label={getImproveStatusText(row)} color={improvementDone ? "success" : "warning"} />
              </Stack>
              <Typography sx={{ fontWeight: 950 }}>{row?.danger_description || "-"}</Typography>
              <Typography variant="body2" color="text.secondary">
                위험성 {getRiskScore(row) || "-"}점 · {row?.manage_criteria || "-"}
              </Typography>
            </Stack>
          </Paper>

          {improvementRequired && !improvementDone ? (
            <Alert severity="warning" sx={{ borderRadius: 2 }}>
              개선 중 항목입니다. 개선 후 사진, 개선 후 위험성, 완료일을 등록한 뒤
              개선 완료 및 승인을 처리할 수 있습니다.
            </Alert>
          ) : null}

          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", md: "repeat(2, minmax(0, 1fr))" },
              gap: 1.5,
            }}
          >
            <Paper elevation={0} sx={{ p: 1.5, border: "1px solid", borderColor: "divider", borderRadius: 2, bgcolor: "#fff" }}>
              <Stack spacing={1.25}>
                <MiniSectionTitle label="개선 전" />
                <EvidencePreview label="개선 전 사진" value={beforeUrl} emptyText="개선 전 사진 없음" />
                <DetailField label="평가 당시 위험성" value={`${getRiskScore(row) || "-"}점 · ${row?.manage_criteria || "-"}`} />
                <DetailField label="개선대책" value={form?.improve_plan || row?.improve_plan || "-"} />
                <DetailField label="담당자" value={findEmployeeName(employees, assignee)} />
                <DetailField label="개선 예정일" value={form?.expected_date || row?.expected_date || "-"} />
              </Stack>
            </Paper>

            <Paper
              elevation={0}
              sx={{
                p: 1.5,
                border: "1px solid",
                borderColor: improvementDone ? "success.light" : "warning.light",
                borderRadius: 2,
                bgcolor: improvementDone ? "#f0fdf4" : "#fffaf0",
              }}
            >
              <Stack spacing={1.25}>
                <MiniSectionTitle label="개선 후" color={improvementDone ? "success.dark" : "warning.dark"} />
                <EvidencePreview
                  label="개선 후 사진"
                  value={afterUrl}
                  emptyText={improvementDone ? "개선 후 사진 없음" : "등록 필요"}
                  required={improvementRequired && !improvementDone}
                />
                <DetailField label="개선 후 위험성" value={afterRisk ? `${afterRisk}점` : improvementDone ? "-" : "등록 필요"} />
                <DetailField label="완료일" value={completeDate || (improvementDone ? "-" : "등록 필요")} />
                <DetailField label="승인일" value={row?.approve_date || "-"} />
                <DetailField label="완료 비고" value={completionNote || "-"} />
              </Stack>
            </Paper>
          </Box>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} sx={{ fontWeight: 900 }}>
          닫기
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function Step4Row({
  row,
  form,
  employees,
  saving,
  onFormChange,
  onUpload,
  onSaveImprovement,
  onApprove,
}) {
  const [detailOpen, setDetailOpen] = useState(false);
  const isApproved = toNumber(row?.eval_status) >= 20;
  const improvementRequired = toNumber(row?.requires_improve) === 1;
  const improvementDone = !improvementRequired || toNumber(row?.improve_status) >= 20;
  const canApprove = !isApproved && row?.frequency_id && row?.importancy_id && improvementDone;
  const completionReady = Boolean(
    form?.improve_after &&
      isAfterRiskScoreValid(form?.improve_after_risk) &&
      form?.comp_date &&
      String(form?.completion_note || "").trim()
  );
  const canCompleteImprovement = improvementRequired && !improvementDone;
  const beforeUrl =
    form?.improve_before_preview ||
    form?.improve_before ||
    row?.improve_before ||
    "";
  const afterUrl =
    form?.improve_after_preview ||
    form?.improve_after ||
    row?.improve_after ||
    "";
  const completeDisabledReason = saving
    ? "저장 중입니다."
    : !completionReady
    ? "개선 후 사진, 개선 후 위험점수, 완료일, 완료 비고를 입력하세요."
    : "";
  const stageLabel = isApproved
    ? "승인 완료"
    : canCompleteImprovement
    ? "완료 처리 필요"
    : canApprove
    ? "승인 필요"
    : "진행 중";
  const stageColor = isApproved
    ? "success"
    : canCompleteImprovement
    ? "warning"
    : canApprove
    ? "info"
    : "default";
  const completeDate = form?.comp_date || row?.comp_date || "";
  const completionNote = form?.completion_note || row?.completion_note || "";

  return (
    <>
      <Paper
        elevation={0}
        sx={{
          p: { xs: 1.5, md: 2 },
          border: "1px solid",
          borderColor: isApproved ? "success.light" : canCompleteImprovement ? "warning.light" : "divider",
          borderRadius: 2,
          bgcolor: "#fff",
          boxShadow: "0 10px 26px rgba(15, 23, 42, 0.04)",
          transition: "border-color 160ms ease, box-shadow 160ms ease, transform 160ms ease",
          "&:hover": {
            borderColor: isApproved ? "success.main" : canCompleteImprovement ? "warning.main" : "primary.light",
            boxShadow: "0 14px 34px rgba(15, 23, 42, 0.07)",
            transform: "translateY(-1px)",
          },
        }}
      >
        <Stack spacing={1.5}>
          <Stack
            direction={{ xs: "column", md: "row" }}
            spacing={1.25}
            alignItems={{ xs: "stretch", md: "flex-start" }}
            justifyContent="space-between"
          >
            <Stack spacing={0.75} sx={{ minWidth: 0, flex: 1 }}>
              <RowTitle row={row} />
              <Tooltip title={row?.danger_description || ""} placement="top-start">
                <Typography sx={{ fontWeight: 900 }} noWrap>
                  {row?.danger_description || "-"}
                </Typography>
              </Tooltip>
            </Stack>

            <Stack direction="row" spacing={0.75} alignItems="center" flexWrap="wrap" useFlexGap justifyContent="flex-end">
              <StatusChip label={stageLabel} color={stageColor} />
              {completeDate ? <StatusChip label={`완료일 ${completeDate}`} color="success" /> : null}
              {!isApproved ? (
                <Tooltip
                  title={
                    canApprove
                      ? ""
                      : !improvementDone
                      ? "개선 완료 후 승인할 수 있습니다."
                      : "위험성평가 등록 후 승인할 수 있습니다."
                  }
                >
                  <span>
                    <Button
                      size="small"
                      variant="contained"
                      color="success"
                      disabled={!canApprove || saving}
                      onClick={() => onApprove(row)}
                      sx={{ fontWeight: 900, whiteSpace: "nowrap" }}
                    >
                      승인
                    </Button>
                  </span>
                </Tooltip>
              ) : null}
            </Stack>
          </Stack>

          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: {
                xs: "1fr",
                lg: canCompleteImprovement
                  ? "minmax(300px, 0.9fr) minmax(420px, 1.1fr)"
                  : "minmax(0, 1fr)",
              },
              gap: 1.25,
            }}
          >
            <Paper
              elevation={0}
              sx={{
                p: 1.25,
                border: "1px solid",
                borderColor: "divider",
                borderRadius: 1.5,
                bgcolor: "#f8fafc",
              }}
            >
              <Stack spacing={1}>
                <MiniSectionTitle label={canCompleteImprovement ? "개선 전" : "개선 정보"} />
                {improvementRequired || row?.improve_id ? (
                  canCompleteImprovement ? (
                    <Box
                      sx={{
                        display: "grid",
                        gridTemplateColumns: { xs: "1fr", sm: "220px minmax(0, 1fr)" },
                        gap: 1.25,
                        alignItems: "stretch",
                      }}
                    >
                      <EvidencePreview compact hideLabel label="개선 전 사진" value={beforeUrl} emptyText="없음" imageHeight={156} />
                      <Paper
                        elevation={0}
                        sx={{
                          p: 1.25,
                          border: "1px solid",
                          borderColor: "divider",
                          borderRadius: 1.25,
                          bgcolor: "#fff",
                        }}
                      >
                        <Box
                          sx={{
                            display: "grid",
                            gridTemplateColumns: { xs: "1fr", sm: "repeat(2, minmax(0, 1fr))" },
                            gap: 1,
                          }}
                        >
                          <DetailField label="현재 위험성" value={`${getRiskScore(row) || "-"}점 · ${row?.manage_criteria || "-"}`} />
                          <DetailField label="평가일" value={row?.eval_date || "-"} />
                          <DetailField label="개선 예정일" value={form?.expected_date || row?.expected_date || "-"} />
                          <DetailField label="담당자" value={findEmployeeName(employees, form?.emp_id || row?.emp_id)} />
                        </Box>
                        <Box sx={{ mt: 1 }}>
                          <DetailField label="개선대책" value={form?.improve_plan || row?.improve_plan || "-"} />
                        </Box>
                      </Paper>
                    </Box>
                  ) : (
                    <Box
                      sx={{
                        display: "grid",
                        gridTemplateColumns: { xs: "1fr", md: "260px minmax(0, 1fr)" },
                        gap: 1.25,
                        alignItems: "stretch",
                      }}
                    >
                      <Box
                        sx={{
                          display: "grid",
                          gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
                          gap: 1,
                        }}
                      >
                        <EvidencePreview compact label="개선 전" value={beforeUrl} emptyText="없음" imageHeight={136} />
                        <EvidencePreview compact label="개선 후" value={afterUrl} emptyText="없음" imageHeight={136} />
                      </Box>
                      <Paper
                        elevation={0}
                        sx={{
                          p: 1.25,
                          border: "1px solid",
                          borderColor: "success.light",
                          borderRadius: 1.5,
                          bgcolor: "#fff",
                        }}
                      >
                        <Stack spacing={1}>
                          <Box
                            sx={{
                              display: "grid",
                              gridTemplateColumns: { xs: "1fr", sm: "repeat(3, minmax(0, 1fr))" },
                              gap: 1,
                            }}
                          >
                            <DetailField label="평가 당시 위험성" value={`${getRiskScore(row) || "-"}점 · ${row?.manage_criteria || "-"}`} />
                            <DetailField label="개선 후 위험성" value={row?.improve_after_risk ? `${row.improve_after_risk}점` : "-"} />
                            <DetailField label="승인 상태" value={isApproved ? "승인 완료" : "승인 필요"} />
                            <DetailField label="평가일" value={row?.eval_date || "-"} />
                            <DetailField label="완료일" value={completeDate || "-"} />
                            <DetailField label="승인일" value={row?.approve_date || "-"} />
                          </Box>
                          <Divider />
                          <Box
                            sx={{
                              display: "grid",
                              gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                              gap: 1,
                            }}
                          >
                            <DetailField label="담당자" value={findEmployeeName(employees, form?.emp_id || row?.emp_id)} />
                            <DetailField label="개선 예정일" value={form?.expected_date || row?.expected_date || "-"} />
                          </Box>
                          <DetailField label="개선대책" value={form?.improve_plan || row?.improve_plan || "-"} />
                          <DetailField label="완료 비고" value={completionNote || "-"} />
                        </Stack>
                      </Paper>
                    </Box>
                  )
                ) : (
                  <Paper
                    elevation={0}
                    sx={{
                      p: 1.25,
                      border: "1px solid",
                      borderColor: "divider",
                      borderRadius: 1.5,
                      bgcolor: "#fff",
                    }}
                  >
                    <Box
                      sx={{
                        display: "grid",
                        gridTemplateColumns: { xs: "1fr", sm: "repeat(3, minmax(0, 1fr))" },
                        gap: 1,
                      }}
                    >
                      <DetailField label="평가 결과" value="현재 안전대책 유지" />
                      <DetailField label="위험성" value={`${getRiskScore(row) || "-"}점 · ${row?.manage_criteria || "-"}`} />
                      <DetailField label="승인 상태" value={isApproved ? "승인 완료" : "승인 필요"} />
                      <DetailField label="평가일" value={row?.eval_date || "-"} />
                      <DetailField label="승인일" value={row?.approve_date || "-"} />
                      <DetailField label="평가 구분" value={row?.eval_scope === "line" ? "라인 평가" : "설비 평가"} />
                    </Box>
                  </Paper>
                )}

                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<VisibilityIcon />}
                  onClick={() => setDetailOpen(true)}
                  sx={{ alignSelf: "flex-start", fontWeight: 900 }}
                >
                  상세보기
                </Button>
              </Stack>
            </Paper>

            {canCompleteImprovement ? (
              <Paper
                elevation={0}
                sx={{
                  p: 1.25,
                  border: "1px solid",
                  borderColor: "warning.light",
                  borderRadius: 1.5,
                  bgcolor: "#fffaf0",
                }}
              >
                <Stack spacing={1}>
                  <MiniSectionTitle label="개선 후 완료 처리" color="warning.dark" />
                  <EvidencePreview
                    compact
                    hideLabel
                    label="개선 후 사진"
                    value={afterUrl}
                    emptyText="개선 후 사진 필요"
                    required
                    imageHeight={126}
                  />
                  <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                    <Button
                      component="label"
                      size="small"
                      variant="outlined"
                      color={form?.improve_after ? "success" : "warning"}
                      startIcon={<UploadFileIcon />}
                      sx={{ fontWeight: 900 }}
                    >
                      개선 후 사진
                      <input
                        hidden
                        type="file"
                        accept="image/*"
                        onChange={(event) =>
                          onUpload(row, "improve_after", event.target.files?.[0])
                        }
                      />
                    </Button>
                    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 800 }}>
                      {form?.improve_after ? "사진 등록됨" : "사진 필요"}
                    </Typography>
                  </Stack>

                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: { xs: "1fr", sm: "minmax(220px, 1fr) minmax(180px, 0.8fr)" },
                      gap: 1,
                      alignItems: "stretch",
                    }}
                  >
                    <TextField
                      size="small"
                      type="number"
                      label="개선 후 위험점수"
                      placeholder="예: 4"
                      helperText="개선 후 위험점수(1~20)를 입력하세요."
                      value={form?.improve_after_risk || ""}
                      onChange={(event) =>
                        onFormChange(row, "improve_after_risk", event.target.value)
                      }
                      inputProps={{ min: 1, max: 20 }}
                      sx={{
                        minWidth: 0,
                        "& .MuiInputBase-root": { minHeight: 58, bgcolor: "#fff" },
                        "& .MuiFormHelperText-root": { mx: 0, fontWeight: 700 },
                      }}
                    />
                    <TextField
                      size="small"
                      type="date"
                      label="완료일"
                      value={form?.comp_date || ""}
                      onChange={(event) => onFormChange(row, "comp_date", event.target.value)}
                      InputLabelProps={{ shrink: true }}
                      sx={{
                        minWidth: 0,
                        "& .MuiInputBase-root": { minHeight: 52, bgcolor: "#fff" },
                      }}
                    />
                  </Box>
                  <TextField
                    fullWidth
                    multiline
                    minRows={2}
                    size="small"
                    label="완료 비고"
                    placeholder="개선 완료 내용이나 확인 메모를 입력하세요."
                    value={form?.completion_note || ""}
                    onChange={(event) => onFormChange(row, "completion_note", event.target.value)}
                    sx={{
                      "& .MuiInputBase-root": { bgcolor: "#fff", alignItems: "flex-start" },
                    }}
                  />
                  <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
                    <Tooltip title={completeDisabledReason} disableHoverListener={!completeDisabledReason}>
                      <span>
                        <Button
                          size="small"
                          variant="contained"
                          color="warning"
                          startIcon={<SaveIcon />}
                          disabled={saving || !completionReady}
                          onClick={() => onSaveImprovement(row, { complete: true })}
                          sx={{ fontWeight: 900, whiteSpace: "nowrap" }}
                        >
                          {saving ? "저장 중" : "개선 완료 및 승인"}
                        </Button>
                      </span>
                    </Tooltip>
                  </Box>
                </Stack>
              </Paper>
            ) : null}
          </Box>
        </Stack>
      </Paper>
      <ImprovementDetailDialog
        open={detailOpen}
        onClose={() => setDetailOpen(false)}
        row={row}
        form={form}
        employees={employees}
        improvementDone={improvementDone}
        improvementRequired={improvementRequired}
      />
    </>
  );
}

function filterRowsByStep(rows, step) {
  if (step === 2) {
    return rows.filter(
      (row) =>
        row.frequency_id &&
        row.importancy_id &&
        !isFinalizedEvaluationRow(row) &&
        (toNumber(row?.requires_improve) === 1 || row?.improve_id || row?.improve_plan)
    );
  }
  if (step === 3 || step === 4) {
    return rows.filter((row) => row.frequency_id && row.importancy_id);
  }
  return rows;
}

const getSearchTextFromRow = (row = {}) => {
  const items = row.__lineGroup ? row.items || [] : [row];
  return items
    .flatMap((item) => [
      item?.danger_description,
      item?.danger_category,
      item?.danger_cause,
      item?.manage_criteria,
      getScopeName(item),
      getMachineName(item),
      getProcessName(item),
      item?.source_location_name,
      item?.location_summary,
      item?.location_names,
    ])
    .filter(Boolean)
    .join(" ")
    .toLowerCase();
};

const rowMatchesSearch = (row, searchText) => {
  const keyword = String(searchText || "").trim().toLowerCase();
  if (!keyword) return true;
  return getSearchTextFromRow(row).includes(keyword);
};

const isInteractiveRowClick = (event) =>
  Boolean(
    event?.target?.closest?.(
      "button,input,textarea,label,[role='button'],.MuiSelect-select,.MuiInputBase-root,.MuiFormControl-root,.MuiTextField-root,.MuiCheckbox-root,.MuiIconButton-root"
    )
  );

const stopRowClickPropagation = (event) => {
  event?.stopPropagation?.();
};

const step1SelectInteractionProps = {
  onClick: stopRowClickPropagation,
  onMouseDown: stopRowClickPropagation,
  onTouchStart: stopRowClickPropagation,
  SelectProps: {
    MenuProps: {
      onClick: stopRowClickPropagation,
      onMouseDown: stopRowClickPropagation,
      onTouchStart: stopRowClickPropagation,
      MenuListProps: {
        onClick: stopRowClickPropagation,
        onMouseDown: stopRowClickPropagation,
        onTouchStart: stopRowClickPropagation,
      },
    },
  },
};

const rowMatchesSummaryFilter = (row, step, filterKey) => {
  if (!filterKey || filterKey === "all") return true;
  if (filterKey === "line") return Boolean(row.__lineGroup || row.eval_scope === "line");

  if (step === 1) {
    if (row.__lineGroup) {
      const items = row.items || [];
      if (filterKey === "pending") return items.some((item) => !isStep1EvaluationComplete(item));
      if (filterKey === "evaluated") return items.length > 0 && items.every(isStep1EvaluationComplete);
    }
    if (filterKey === "pending") return !row?.eval_id;
    if (filterKey === "evaluated") return Boolean(row.frequency_id && row.importancy_id);
  }

  if (step === 2) {
    const saved = Boolean(row?.improve_id || row?.improve_plan);
    if (filterKey === "missing") return !saved;
    if (filterKey === "saved") return saved;
  }

  if (step === 3 || step === 4) {
    const improvementRequired = toNumber(row?.requires_improve) === 1 || row?.improve_id;
    const improvementDone = !improvementRequired || toNumber(row?.improve_status) >= 20;
    if (filterKey === "completion") return improvementRequired && !improvementDone;
    if (filterKey === "approved") return toNumber(row?.eval_status) >= 20;
  }

  return true;
};

function createInitialForms(rows) {
  return Object.fromEntries(
    rows.map((row) => [
      getRowKey(row),
      {
        frequency_id: row.frequency_id || "",
        importancy_id: row.importancy_id || "",
        improve_plan: row.improve_plan || "",
        improve_before: row.improve_before || "",
        improve_after: row.improve_after || "",
        emp_id: row.emp_id || "",
        improve_after_risk: row.improve_after_risk || "",
        expected_date: row.expected_date || "",
        comp_date: row.comp_date || "",
        completion_note: row.completion_note || "",
      },
    ])
  );
}

function RiskAssessmentStepPage({ step }) {
  const { group_code, project_code } = useParams();
  const config = stepConfigs[step] || stepConfigs[1];
  const [evalMonth, setEvalMonth] = useState(todayMonthText());
  const evalDate = monthToEvalDate(evalMonth);
  const [rows, setRows] = useState([]);
  const [criteria, setCriteria] = useState({ frequencies: [], importancies: [], standards: [] });
  const [employees, setEmployees] = useState([]);
  const [forms, setForms] = useState({});
  const [selected, setSelected] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [savingKey, setSavingKey] = useState("");
  const [feedback, setFeedback] = useState(null);
  const [reloadKey, setReloadKey] = useState(0);
  const [searchText, setSearchText] = useState("");
  const [activeSummaryFilter, setActiveSummaryFilter] = useState("all");
  const [step1DetailRow, setStep1DetailRow] = useState(null);
  // The permanent sidebar leaves a portrait tablet with less than 700px of
  // usable content width. Switch to the card layout before the table becomes
  // cramped, while keeping the desktop and landscape-tablet table layout.
  const isCompact = useMediaQuery((theme) => theme.breakpoints.down("lg"));

  const reload = () => setReloadKey((prev) => prev + 1);

  useEffect(() => {
    if (!group_code || !project_code) return undefined;

    let ignore = false;
    setIsLoading(true);
    setFeedback(null);

    const criteriaRequest = axiosInstance.get(`/risk/${group_code}/${project_code}/wizard/risk-criteria`);
    const stepRequest = axiosInstance
      .get(`/risk/${group_code}/${project_code}/wizard/step-items`, {
        params: { eval_date: evalDate },
      })
      .catch((error) => {
        if (error?.response?.status === 404) {
          return axiosInstance.get(`/risk/${group_code}/${project_code}/wizard/work-items`, {
            params: { eval_date: evalDate },
          });
        }
        throw error;
      });
    const pendingRequest =
      step === 1
        ? axiosInstance.get(`/risk/${group_code}/${project_code}/wizard/pending-hazards`, {
            params: { eval_date: evalDate },
          })
        : Promise.resolve({ data: { rows: [] } });

    Promise.all([criteriaRequest, stepRequest, pendingRequest])
      .then(([criteriaResponse, stepResponse, pendingResponse]) => {
        if (ignore) return;

        const savedRows = (stepResponse.data?.rows || []).map((row) => ({ ...row, __saved: true }));
        const pendingRows = (pendingResponse.data?.rows || []).map((row) => ({
          ...row,
          eval_scope: row.eval_scope || "asset",
          __saved: false,
        }));

        const baseRows = step === 1 ? [...pendingRows, ...savedRows] : savedRows;
        const nextRows = withStepLineRows(filterRowsByStep(baseRows, step), step);

        setCriteria({
          frequencies: criteriaResponse.data?.frequencies || [],
          importancies: criteriaResponse.data?.importancies || [],
          standards: criteriaResponse.data?.standards || [],
        });
        setEmployees(stepResponse.data?.employees || []);
        setRows(nextRows);
        setForms(createInitialForms(nextRows));
        setSelected({});
        setActiveSummaryFilter("all");
      })
      .catch((error) => {
        if (ignore) return;
        setRows([]);
        setFeedback({
          severity: "error",
          message:
            error?.response?.data?.message ||
            error?.message ||
            "단계 데이터를 불러오지 못했습니다.",
        });
      })
      .finally(() => {
        if (!ignore) setIsLoading(false);
      });

    return () => {
      ignore = true;
    };
  }, [evalDate, group_code, project_code, reloadKey, step]);

  const summary = useMemo(() => buildSummary(rows, step), [rows, step]);
  const displayedRows = useMemo(
    () =>
      rows.filter(
        (row) =>
          rowMatchesSummaryFilter(row, step, activeSummaryFilter) &&
          rowMatchesSearch(row, searchText)
      ),
    [activeSummaryFilter, rows, searchText, step]
  );

  const handleSummaryClick = (item) => {
    if (!item.count) {
      setFeedback({
        severity: "info",
        message: `${item.label} 항목이 없습니다.`,
      });
      return;
    }
    setActiveSummaryFilter(item.key || "all");
  };

  const updateForm = (row, field, value) => {
    const key = getRowKey(row);
    setForms((prev) => ({
      ...prev,
      [key]: {
        ...(prev[key] || {}),
        [field]: value,
      },
    }));
  };

  const uploadFile = async (row, field, file) => {
    if (!file) return;
    const key = getRowKey(row);
    const previewField = `${field}_preview`;
    const previewUrl =
      typeof URL !== "undefined" &&
      typeof Blob !== "undefined" &&
      file instanceof Blob
        ? URL.createObjectURL(file)
        : "";
    if (previewUrl) updateForm(row, previewField, previewUrl);
    setSavingKey(`${key}-${field}`);
    try {
      const formData = new FormData();
      formData.append("file", file);
      formData.append("kind", field);
      const response = await axiosInstance.post(
        `/risk/${group_code}/${project_code}/wizard/file`,
        formData,
        { headers: { "Content-Type": "multipart/form-data" } }
      );
      updateForm(row, field, response.data?.url || "");
      if (previewUrl) {
        updateForm(row, previewField, "");
        URL.revokeObjectURL(previewUrl);
      }
      setFeedback({ severity: "success", message: "사진이 업로드되었습니다." });
    } catch (error) {
      if (previewUrl) {
        updateForm(row, previewField, "");
        URL.revokeObjectURL(previewUrl);
      }
      setFeedback({
        severity: "error",
        message: error?.response?.data?.message || "사진 업로드 중 오류가 발생했습니다.",
      });
    } finally {
      setSavingKey("");
    }
  };

  const saveEvaluation = async (row, overrides = {}) => {
    const key = getRowKey(row);
    const form = forms[key] || {};
    const targetRows = row.__lineGroup
      ? (row.items || []).filter((item) => !isStep1EvaluationComplete(item))
      : [row];
    const missingLineInputs = row.__lineGroup
      ? targetRows.filter((targetRow) => {
          const targetForm = forms[getRowKey(targetRow)] || {};
          return !targetForm.frequency_id || !targetForm.importancy_id;
        })
      : [];

    if (
      (!row.__lineGroup && (!form.frequency_id || !form.importancy_id)) ||
      (row.__lineGroup && missingLineInputs.length > 0)
    ) {
      setFeedback({
        severity: "warning",
        message: row.__lineGroup
          ? "라인 내 모든 유해·위험요인의 가능성과 중대성을 선택하세요."
          : "가능성과 중대성을 먼저 선택하세요.",
      });
      return;
    }

    setSavingKey(key);
    try {
      if (!targetRows.length) {
        setFeedback({ severity: "info", message: "이미 평가 완료된 항목입니다." });
        setSavingKey("");
        return;
      }
      const evalScope = row.__lineGroup ? "line" : row.eval_scope || "asset";
      const sourceLocationId = row.__lineGroup
        ? row.source_location_id || null
        : row.source_location_id || null;
      const wizardGroupKey = row.__lineGroup
        ? `line:${evalDate}:${sourceLocationId || row.source_location_name || row.__lineGroupKey}`
        : row.wizard_group_key || null;

      await axiosInstance.post(`/risk/${group_code}/${project_code}/wizard/risk-evaluations`, {
        eval_date: evalDate,
        evaluations: targetRows.map((targetRow) => {
          const targetForm = row.__lineGroup ? forms[getRowKey(targetRow)] || {} : form;
          return {
            eval_id: targetRow.eval_id,
            register_cause_id: targetRow.register_cause_id,
            frequency_id: targetForm.frequency_id,
            importancy_id: targetForm.importancy_id,
            eval_status:
              overrides.eval_status ??
              Math.max(toNumber(targetRow.eval_status), 10),
            approve_date: overrides.approve_date || targetRow.approve_date || null,
            eval_date: evalDate,
            eval_scope: evalScope,
            source_location_id: evalScope === "line" ? sourceLocationId : targetRow.source_location_id || null,
            wizard_step: overrides.wizard_step || 2,
            wizard_group_key: wizardGroupKey || targetRow.wizard_group_key || null,
            upsert: true,
            improvement: overrides.improvement,
          };
        }),
      });
      setFeedback({
        severity: "success",
        message: overrides.successMessage || "위험성평가가 등록되었습니다.",
      });
      reload();
    } catch (error) {
      setFeedback({
        severity: "error",
        message: error?.response?.data?.message || "위험성평가 등록 중 오류가 발생했습니다.",
      });
    } finally {
      setSavingKey("");
    }
  };

  const saveImprovement = async (row, options = {}) => {
    const key = getRowKey(row);
    const form = forms[key] || {};
    const complete = Boolean(options.complete);
    const planSaved = Boolean(row.improve_id || row.improve_plan);
    if (!complete && isFinalizedEvaluationRow(row)) {
      setFeedback({
        severity: "info",
        message: "이미 완료된 항목은 STEP 2에서 수정할 수 없습니다. STEP 3에서 상세 내용을 확인하세요.",
      });
      return;
    }
    if (!row.frequency_id || !row.importancy_id) {
      setFeedback({ severity: "warning", message: "STEP 1 위험성평가를 먼저 등록하세요." });
      return;
    }
    if (!form.improve_plan || !form.emp_id || !form.expected_date) {
      setFeedback({ severity: "warning", message: "개선대책, 담당자, 개선 예정일을 입력하세요." });
      return;
    }
    if (
      complete &&
      (!form.improve_after ||
        !isAfterRiskScoreValid(form.improve_after_risk) ||
        !form.comp_date ||
        !String(form.completion_note || "").trim())
    ) {
      setFeedback({
        severity: "warning",
        message: "개선 후 사진, 개선 후 위험점수, 완료일, 완료 비고를 입력하세요.",
      });
      return;
    }

    const keepCompleted = !complete && toNumber(row.improve_status) >= 20;
    await saveEvaluation(row, {
      wizard_step: complete ? 4 : 3,
      eval_status: complete ? 20 : row.eval_status || 10,
      approve_date: complete ? todayText() : row.approve_date || null,
      successMessage: complete
        ? "개선 완료와 승인이 저장되었습니다."
        : planSaved
        ? "개선대책이 수정되었습니다."
        : "개선대책이 등록되었습니다.",
      improvement: {
        improve_status: complete || keepCompleted ? 20 : 10,
        improve_plan: form.improve_plan,
        improve_before: form.improve_before || null,
        improve_after: complete || keepCompleted ? form.improve_after || row.improve_after || null : row.improve_after || null,
        emp_id: form.emp_id,
        improve_after_risk:
          complete || keepCompleted
            ? form.improve_after_risk || row.improve_after_risk || null
            : row.improve_after_risk || null,
        expected_date: form.expected_date,
        comp_date: complete || keepCompleted ? form.comp_date || row.comp_date || null : row.comp_date || null,
        completion_note:
          complete || keepCompleted
            ? form.completion_note || row.completion_note || null
            : row.completion_note || null,
      },
    });
  };

  const approveEvaluation = async (row) => {
    const key = getRowKey(row);
    const form = forms[key] || {};
    const improvementRequired = toNumber(row.requires_improve) === 1;
    if (improvementRequired && toNumber(row.improve_status) < 20) {
      setFeedback({ severity: "warning", message: "개선 완료 후 승인할 수 있습니다." });
      return;
    }

    await saveEvaluation(row, {
      wizard_step: 4,
      eval_status: 20,
      approve_date: todayText(),
      successMessage: "위험성평가가 승인되었습니다.",
      improvement: row.improve_id
        ? {
            improve_status: row.improve_status,
            improve_plan: row.improve_plan,
            improve_before: row.improve_before || form.improve_before || null,
            improve_after: row.improve_after || form.improve_after || null,
            emp_id: row.emp_id || form.emp_id || null,
            improve_after_risk: row.improve_after_risk || form.improve_after_risk || null,
            expected_date: row.expected_date || form.expected_date || null,
            comp_date: row.comp_date || form.comp_date || null,
            completion_note: row.completion_note || form.completion_note || null,
          }
        : undefined,
    });
  };

  const renderRow = (row, index) => {
    const key = getRowKey(row);
    const form = forms[key] || {};
    const saving = savingKey === key || savingKey.startsWith(`${key}-`);

    if (step === 1) {
      const commonProps = {
        row,
        form,
        forms,
        criteria,
        saving,
        selected: Boolean(selected[getSelectionKey(row)]),
        onToggle: (target) =>
          setSelected((prev) => ({
            ...prev,
            [getSelectionKey(target)]: !prev[getSelectionKey(target)],
          })),
        onFormChange: updateForm,
        onSave: saveEvaluation,
        onOpenDetail: setStep1DetailRow,
      };

      return isCompact ? (
        <Step1Card key={`${key}-${index}`} {...commonProps} />
      ) : (
        <Step1Row key={`${key}-${index}`} {...commonProps} />
      );
    }
    if (step === 2) {
      return (
        <Step3Row
          key={`${key}-${index}`}
          row={row}
          form={form}
          employees={employees}
          saving={saving}
          onFormChange={updateForm}
          onUpload={uploadFile}
          onSave={saveImprovement}
        />
      );
    }
    if (step === 3 || step === 4) {
      return (
        <Step4Row
          key={`${key}-${index}`}
          row={row}
          form={form}
          employees={employees}
          saving={saving}
          onFormChange={updateForm}
          onUpload={uploadFile}
          onSaveImprovement={saveImprovement}
          onApprove={approveEvaluation}
        />
      );
    }
    return (
      <Step2Row
        key={`${key}-${index}`}
        row={row}
        form={form}
        criteria={criteria}
        saving={saving}
        onFormChange={updateForm}
        onSave={saveEvaluation}
      />
    );
  };

  const useCardLayout = isCompact || step === 2 || step === 3 || step === 4;

  return (
    <Box sx={{ minHeight: "100%", p: { xs: 1.5, md: 3 }, bgcolor: "#f6f8fb" }}>
      <Stack
        spacing={2}
        sx={{
          width: "100%",
          maxWidth: { xs: 900, lg: 1280 },
          mx: "auto",
        }}
      >
        <Paper
          elevation={0}
          sx={{
            p: { xs: 2, md: 2.5 },
            border: "1px solid",
            borderColor: "divider",
            borderRadius: 2,
            bgcolor: "#fff",
          }}
        >
          <Stack
            direction={{ xs: "column", md: "row" }}
            spacing={2}
            alignItems={{ xs: "stretch", md: "center" }}
            justifyContent="space-between"
          >
            <Stack direction="row" spacing={1.25} alignItems="center">
              <Box
                sx={{
                  width: 42,
                  height: 42,
                  borderRadius: "50%",
                  display: "grid",
                  placeItems: "center",
                  color: config.tone,
                  bgcolor: `${config.tone}14`,
                  flexShrink: 0,
                }}
              >
                {config.icon}
              </Box>
              <Box>
                <Stack direction="row" spacing={0.5} alignItems="center">
                  <Typography variant="h6" sx={{ fontWeight: 900 }}>
                    {config.title}
                  </Typography>
                  <ManualHelpButton title={config.title} />
                </Stack>
                <Typography variant="body2" color="text.secondary">
                  {config.description}
                </Typography>
              </Box>
            </Stack>

            <Stack direction="row" spacing={1} alignItems="center" justifyContent="flex-end">
              <TextField
                label="기준 월"
                size="small"
                type="month"
                value={evalMonth}
                onChange={(event) => setEvalMonth(event.target.value || todayMonthText())}
                InputLabelProps={{ shrink: true }}
                sx={{ width: { xs: "100%", sm: 160 } }}
              />
            </Stack>
          </Stack>
        </Paper>

        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "repeat(2, minmax(0, 1fr))", md: "repeat(4, minmax(0, 1fr))" },
            gap: 1.5,
          }}
        >
          {summary.map(({ key: summaryKey, ...item }) => (
            <StatCard
              key={summaryKey}
              {...item}
              active={activeSummaryFilter === summaryKey}
              disabled={!item.count}
              onClick={() => handleSummaryClick({ key: summaryKey, ...item })}
            />
          ))}
        </Box>

        {feedback ? <Alert severity={feedback.severity}>{feedback.message}</Alert> : null}

        <Paper
          elevation={0}
          sx={{
            border: "1px solid",
            borderColor: "divider",
            borderRadius: 2,
            bgcolor: "#fff",
            overflow: "hidden",
          }}
        >
          <Stack
            direction={{ xs: "column", md: "row" }}
            alignItems={{ xs: "stretch", md: "center" }}
            justifyContent="space-between"
            spacing={1.25}
            sx={{ px: 2, py: 1.5 }}
          >
            <Stack direction="row" spacing={1} alignItems="center">
              <Chip
                size="small"
                label={config.label}
                sx={{ bgcolor: `${config.tone}14`, color: config.tone, fontWeight: 900 }}
              />
              <Typography sx={{ fontWeight: 900 }}>단계별 작업</Typography>
            </Stack>
            <Stack
              direction={{ xs: "column", sm: "row" }}
              spacing={1}
              alignItems={{ xs: "stretch", sm: "center" }}
              justifyContent="flex-end"
            >
              <TextField
                size="small"
                placeholder="대상, 위험요인, 공정 검색"
                value={searchText}
                onChange={(event) => setSearchText(event.target.value)}
                sx={{
                  width: { xs: "100%", sm: 300 },
                  "& .MuiInputBase-root": { bgcolor: "#fff" },
                }}
              />
              {isLoading ? (
                <Stack direction="row" spacing={1} alignItems="center">
                  <CircularProgress size={18} />
                  <Typography variant="body2" color="text.secondary">
                    불러오는 중
                  </Typography>
                </Stack>
              ) : (
                <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: "nowrap" }}>
                  {displayedRows.length}/{rows.length}건
                </Typography>
              )}
            </Stack>
          </Stack>
          <Divider />

          {useCardLayout ? (
            <Box
              sx={{
                p: { xs: 1.25, md: 1.5 },
                maxHeight: { xs: "none", md: "calc(100vh - 360px)" },
                minHeight: { xs: 280, md: 380 },
                overflowY: { xs: "visible", md: "auto" },
                bgcolor: "#f8fafc",
              }}
            >
              {displayedRows.length ? (
                <Stack spacing={1.25}>{displayedRows.map(renderRow)}</Stack>
              ) : (
                <Box
                  sx={{
                    minHeight: 320,
                    display: "grid",
                    placeItems: "center",
                    border: "1px dashed",
                    borderColor: "divider",
                    borderRadius: 2,
                    bgcolor: "#fff",
                  }}
                >
                  <Stack spacing={0.75} alignItems="center">
                    <Inventory2Icon sx={{ color: "text.disabled" }} />
                    <Typography sx={{ fontWeight: 900 }}>표시할 항목이 없습니다.</Typography>
                    <Typography variant="body2" color="text.secondary">
                      이전 단계 진행 여부와 기준 월을 확인하세요.
                    </Typography>
                  </Stack>
                </Box>
              )}
            </Box>
          ) : (
            <TableContainer sx={{ maxHeight: "calc(100vh - 360px)", minHeight: 380 }}>
              <Table stickyHeader size="small">
                <TableHead>
                  <TableRow>
                    {config.columns.map((column) => (
                      <TableCell
                        key={column}
                        align="center"
                        sx={{ bgcolor: "#f8fafc", fontWeight: 900, color: "text.secondary", whiteSpace: "nowrap" }}
                      >
                        {column}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {displayedRows.length ? (
                    displayedRows.map(renderRow)
                  ) : (
                    <EmptyRows
                      colSpan={config.columns.length}
                      title="표시할 항목이 없습니다."
                      body="이전 단계 진행 여부와 기준 월을 확인하세요."
                    />
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Paper>
        {step === 1 ? (
          <RiskHistoryDialog
            open={Boolean(step1DetailRow)}
            row={step1DetailRow}
            criteria={criteria}
            onClose={() => setStep1DetailRow(null)}
          />
        ) : null}
      </Stack>
    </Box>
  );
}

export function AssessmentStep1Page() {
  return <RiskAssessmentStepPage step={1} />;
}

export function AssessmentStep2Page() {
  return <RiskAssessmentStepPage step={2} />;
}

export function AssessmentStep3Page() {
  return <RiskAssessmentStepPage step={3} />;
}
